import {
  FhirResourceIdMap,
  FormCollection,
  FormCollectionSet,
  ICodeAndLinkIdForObservations,
  IDisplayData,
  IPatientEntryData,
  ObservationResourceIdMap,
  StentDataMap
} from 'src/interfaces/utility-interfaces';

import { AppConstants } from 'src/constants/constants';
import { BaseInput } from '../shared/models/base-input';
import { ClinicalAdapterService } from 'src/app/services/clinical-adapter.service';
import { ClinicalConstants } from 'src/constants/clinical-constants';
import { DropdownInput } from '../shared/models/dropdown-input';
import { FormGroup } from '@angular/forms';
import { HttpService } from 'src/app/services/http.service';
import { Injectable } from '@angular/core';
import { UtilService } from 'src/app/services/util.service';
import { environment } from 'src/environments/environment';
import { fhirR4 } from '@smile-cdr/fhirts';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root'
})
export class ClinicalService {

  patient: fhirR4.Patient;
  patientReference: fhirR4.Reference;
  transactionBundle: fhirR4.Bundle = {
    resourceType: 'Bundle',
    entry: [],
    type: fhirR4.Bundle.TypeEnum.Transaction
  };
  observationsList: Array<fhirR4.Observation> = [];
  deletedOfflistingObservationList: string[] = [];
  deletedOfflistingDiagnosticReportList: FhirResourceIdMap [] = [];
  initialSupportingInfoList: Array<fhirR4.Reference> = [];
  patientPostalCodeOriginalValue: string = null;
  entryId: string = null;
  listOfObservationsToBeDeleted = [];

  constructor(
    private utilService: UtilService,
    private httpService: HttpService,
    private clinicalAdapterService: ClinicalAdapterService
    ) { }

    // #3571 If an Entry has an identifier, the identifier should be displayed. This is for migrated entries
    // Added entry Identifier
  createClinicalResources(
    formCollection: FormCollectionSet,
    patientID: string,
    entryIdentifier: string): Promise<fhirR4.Bundle> {

    this.resetTransactionBundle();
    this.patientReference = {
      reference: 'Patient/' + patientID
    };
    const serviceRequestReference: fhirR4.Reference = this.buildServiceRequestFromFormCollection(formCollection, entryIdentifier);
    const listOfAppointments: fhirR4.Appointment[] = this.processAcceptanceInformation(formCollection.acceptance);
    const deletedAppointments = this.getDeletedAppointments(listOfAppointments, formCollection.acceptance);
    const diagnosticReport: fhirR4.DiagnosticReport = this.processDiagnosticsInformation(formCollection.diagnostics);

    // Offlisting
    this.deletedOfflistingDiagnosticReportList = [];
    this.deletedOfflistingObservationList = [];

    const listOfProcedures: fhirR4.Procedure[] = this.processOfflistingInformation(formCollection.offlisting);
    const deletedProcedures = this.getDeletedProcedures(listOfProcedures, formCollection.offlisting);
    deletedProcedures?.forEach(procedure => {
      const reportsMap: FhirResourceIdMap[] =
      formCollection.offlisting.diagnosticReportProcedureFhirResourceIds?.filter(x => x.index === procedure.index);
      // Check for duplicates before adding DiagnosticReport Procedure to the deletedOfflistingDiagnosticReportList
      if (reportsMap?.length > 0) {
        if (this.deletedOfflistingDiagnosticReportList?.length === 0) {
          this.deletedOfflistingDiagnosticReportList = this.deletedOfflistingDiagnosticReportList.concat(reportsMap);
        } else {
          reportsMap.forEach(item => {
            if (!this.deletedOfflistingDiagnosticReportList.find(x => x.fhirId === item.fhirId)) {
              this.deletedOfflistingDiagnosticReportList.push(item);
            }
          });
        }
      }
    });

    const bundle: Promise<fhirR4.Bundle> =
    this.handleTransactions(listOfAppointments, diagnosticReport, listOfProcedures,
      deletedAppointments, deletedProcedures, serviceRequestReference, formCollection.diagnostics.diagnosticReportFhirResourceId);
    return bundle;
  }

  resetTransactionBundle() {
    this.patientPostalCodeOriginalValue = null;
    this.entryId = null;
    this.transactionBundle.entry = [];
    this.observationsList = [];
    this.deletedOfflistingObservationList = [];
    this.deletedOfflistingDiagnosticReportList = [];
    this.initialSupportingInfoList = [];
    this.patientPostalCodeOriginalValue = null;
    this.listOfObservationsToBeDeleted = [];
  }

  // #3571 If an Entry has an identifier, the identifier should be displayed. This is for migrated entries
  // Added entry Identifier to method signature
  private buildServiceRequestFromFormCollection(formCollection: FormCollectionSet, entryIdentifier: string): fhirR4.Reference {
    // Entry ID
    const serviceFormCollection = formCollection.service;
    const referralFormCollection = formCollection.referral;
    const waitTimeFormCollection = formCollection.waitTime;
    const acceptanceFormCollection = formCollection.acceptance;
    const offlistingFormCollection = formCollection.offlisting;

    const serviceRequest: fhirR4.ServiceRequest = {
      resourceType: 'ServiceRequest',
      subject: this.patientReference,
      intent: AppConstants.SERVICE_REQUEST_INTENT,
      supportingInfo: []
    };

    // #3571 If an Entry has an identifier, the identifier should be displayed. This is for migrated entries
    if (entryIdentifier) {
      this.entryId = entryIdentifier;
      const identifier: fhirR4.Identifier = new fhirR4.Identifier();
      identifier.system = AppConstants.ENTRY_IDENTIFIER_SYSTEM;
      identifier.value = entryIdentifier;
      serviceRequest.identifier = [identifier];
    }

    // 154 Service Location
    const serviceLocation = this.getValueFromFormCollection(serviceFormCollection, AppConstants.serviceLocationLinkId);
    if (serviceLocation) {
      const serviceLocationReference: fhirR4.Reference = {
        reference: serviceLocation,
        type: 'Organization'
      };
      if (serviceRequest.performer) {
        serviceRequest.performer.push(serviceLocationReference);
      } else {
        serviceRequest.performer = [serviceLocationReference];
      }
    }

    // 156. Responsible Health Care Professional
    const responsibleHealthCareProfessional = acceptanceFormCollection.form.get(AppConstants.responsibleHealthCareProfessionalLinkId).value;
    if (responsibleHealthCareProfessional) {
      const responsibleHealthCareProfessionalReference: fhirR4.Reference = {
        reference: responsibleHealthCareProfessional,
        type: 'Practitioner'
      };
      if (serviceRequest.performer) {
        serviceRequest.performer.push(responsibleHealthCareProfessionalReference);
      } else {
        serviceRequest.performer = [responsibleHealthCareProfessionalReference];
      }
    }

    // Service Detail 2
    const serviceCode = this.buildServiceCode(serviceFormCollection);
    if (serviceCode) {
      serviceRequest.code = this.setCodeableConcept(serviceCode, AppConstants.serviceDetailCodeSystem, 'Service Detail');
    }
    // Entry status
    const status = formCollection.waitTime.form.get(AppConstants.entryStatusLinkId).value;
    if (status) {
      if (status === 'O') {
        serviceRequest.status = 'active';
      } else if (status === 'C') {
        serviceRequest.status = 'completed';
      }
    }

    // 33 Referral Date
    const referralDate = formCollection.referral.form.get(AppConstants.referralDateLinkId).value;
    if (referralDate) {
      serviceRequest.authoredOn = referralDate;
    }

    // 38 Primary Reason for Referral
    const referralPrimaryReason = formCollection.referral.form.get(AppConstants.primaryReasonReferralLinkId).value;
    if (referralPrimaryReason) {
      serviceRequest.reasonCode = [this.setCodeableConcept(referralPrimaryReason,
        AppConstants.REFERRAL_REASON_CODE_SYSTEM, 'Primary Reason for Referral')];
    }

    // 39 Primary Reason for Referral Type
    // const referralPrimaryReasonType = formCollection.referral.form.get(AppConstants.primaryReasonReferralTypeLinkId).value;
    const referralPrimaryReasonType =
    this.getValueFromFormCollection(formCollection.referral, AppConstants.primaryReasonReferralTypeLinkId);
    if (referralPrimaryReasonType) {
      serviceRequest.reasonCode.push(this.setCodeableConcept(referralPrimaryReasonType,
        AppConstants.REFERRAL_REASON_TYPE_CODE_SYSTEM, 'Primary Reason for Referral Type'));
    }

    // 155 Referring Physician
    const referringPhysician = formCollection.referral.form.get(AppConstants.referringPhysicianLinkId).value;
    if (referringPhysician) {
      serviceRequest.requester = {
        reference: referringPhysician
      };
    }

    // 160 Wait Location
    const waitLocation = this.getValueFromFormCollection(referralFormCollection, AppConstants.waitLocationLinkId);
    if (waitLocation) {
      const waitLocationReference: fhirR4.Reference = {
        reference: waitLocation
      };
      if (serviceRequest.supportingInfo) {
        serviceRequest.supportingInfo.push(waitLocationReference);
      } else {
        serviceRequest.supportingInfo = [waitLocationReference];
      }
    }

    // 103. Acceptance Date
    const acceptanceDate = acceptanceFormCollection.form.get(AppConstants.acceptanceDateLinkId).value;
    if (acceptanceDate) {
      if (serviceRequest.occurrencePeriod) {
        serviceRequest.occurrencePeriod.start = acceptanceDate;
      } else {
        const occurrencePeriod: fhirR4.Period = {
          start: acceptanceDate
        };
        serviceRequest.occurrencePeriod = occurrencePeriod;
      }
    }

    // 112. Removal Date
    const removalDate = offlistingFormCollection.form.get(AppConstants.removalDateFieldLinkId).value;
    if (removalDate) {
      if (serviceRequest.occurrencePeriod) {
        serviceRequest.occurrencePeriod.end = removalDate;
      } else {
        const occurrencePeriod: fhirR4.Period = {
          end: removalDate
        };
        serviceRequest.occurrencePeriod = occurrencePeriod;
      }
    }

    const referralObservationsEntriesList: fhirR4.BundleEntry[] =
      this.buildObservationsForGivenSection(
        referralFormCollection,
        referralFormCollection.data,
        ClinicalConstants.referralObservationsList
      );
    if (referralObservationsEntriesList.length > 0) {
      const referralObservationReferenceList: fhirR4.Reference[] =
        this.buildReferenceListFromBundleEntries(referralObservationsEntriesList, 'Observation');
        if (referralObservationReferenceList.length > 0) {
          serviceRequest.supportingInfo = serviceRequest.supportingInfo.concat(referralObservationReferenceList);
        }
    }

    const waitTimesObservationsEntriesList: fhirR4.BundleEntry[] =
      this.buildObservationsForGivenSection(
        waitTimeFormCollection,
        waitTimeFormCollection.data,
        ClinicalConstants.waitTimesObservationsList
      );
    if (waitTimesObservationsEntriesList.length > 0) {
      const waitTimesObservationReferenceList: fhirR4.Reference[] =
        this.buildReferenceListFromBundleEntries(waitTimesObservationsEntriesList, 'Observation');
        if (waitTimesObservationReferenceList.length > 0) {
          serviceRequest.supportingInfo = serviceRequest.supportingInfo.concat(waitTimesObservationReferenceList);
        }
    }

    // Get parts of Acceptance info that should be mapped to SR
    const acceptanceObservationsEntriesList: fhirR4.BundleEntry[] = this.buildObservationsForGivenSection(acceptanceFormCollection,
      acceptanceFormCollection.data, ClinicalConstants.acceptanceObservationsListForServiceRequest);
      if (acceptanceObservationsEntriesList.length > 0) {
        const acceptanceObservationReferenceList: fhirR4.Reference[] =
        this.buildReferenceListFromBundleEntries(acceptanceObservationsEntriesList, 'Observation');
        if (acceptanceObservationReferenceList.length > 0) {
          serviceRequest.supportingInfo = serviceRequest.supportingInfo.concat(acceptanceObservationReferenceList);
        }
      }

    // Get parts of Offlisting and Procedure section that should be mapped to SR
    const offlistingObservationsEntriesList: fhirR4.BundleEntry[] = this.buildObservationsForGivenSection(offlistingFormCollection,
      offlistingFormCollection.data, ClinicalConstants.offlistingObservationsListForServiceRequest);
      if (offlistingObservationsEntriesList.length > 0) {
        const offlistingObservationReferenceList: fhirR4.Reference[] =
        this.buildReferenceListFromBundleEntries(offlistingObservationsEntriesList, 'Observation');
        if (offlistingObservationReferenceList.length) {
          serviceRequest.supportingInfo = serviceRequest.supportingInfo.concat(offlistingObservationReferenceList);
        }
      }

    const serviceRequestReference: fhirR4.Reference = new fhirR4.Reference();

    if (serviceFormCollection.serviceRequestFhirResourceId) {
      serviceRequestReference.reference = 'ServiceRequest/' + serviceFormCollection.serviceRequestFhirResourceId;
      serviceRequest.id = serviceFormCollection.serviceRequestFhirResourceId;
      // #3599 - Replace meta tag indices for Procedure elements
      if (!this.entryId) {
        this.entryId = serviceRequest.id;
      }
      this.addIndividualResourceToFirstTransactionBundle(serviceRequest, 'PUT');
    } else {
      const serviceRequestBundleEntry: fhirR4.BundleEntry = this.addIndividualResourceToFirstTransactionBundle(serviceRequest, 'POST');
      serviceRequestReference.reference = serviceRequestBundleEntry.fullUrl;
    }

    return serviceRequestReference;
  }

  buildServiceCode(serviceFormCollection: FormCollection): string {
    let serviceCode = '';
    const serviceCategory = this.getValueFromFormCollection(serviceFormCollection, AppConstants.entryCategoryLinkId);
    if (serviceCategory) {
      serviceCode += serviceCategory;
    }
    const serviceArea = this.getValueFromFormCollection(serviceFormCollection, AppConstants.serviceAreaLinkId);
    if (serviceArea) {
      serviceCode += '.' + serviceArea;
    }
    const serviceDetail1 = this.getValueFromFormCollection(serviceFormCollection, AppConstants.serviceDetail1LinkId);
    if (serviceDetail1) {
      serviceCode += '.' + serviceDetail1;
    }
    const serviceDetail2 = this.getValueFromFormCollection(serviceFormCollection, AppConstants.serviceDetail2LinkId);
    if (serviceDetail2) {
      serviceCode += '.' + serviceDetail2;
    }
    return serviceCode;
  }

  private processDiagnosticsInformation(diagnosticsFormCollection: FormCollection): fhirR4.DiagnosticReport {
    const codeableConcept: fhirR4.CodeableConcept = this.setCodeableConcept(
      AppConstants.codeValueForDiagnosticReport,
      AppConstants.loincCodeSystemUri,
      AppConstants.codeDisplayForDiagnosticReport);
    const observationsEntriesList: fhirR4.BundleEntry[] = this.buildObservationsForGivenSection(diagnosticsFormCollection,
      diagnosticsFormCollection.data, ClinicalConstants.diagnosticsObservationList);  
    let diagnosticReport: fhirR4.DiagnosticReport = null;
    if (observationsEntriesList.length > 0) {
      const observationReferenceList: fhirR4.Reference[] = this.buildReferenceListFromBundleEntries(observationsEntriesList, 'Observation');
      diagnosticReport = {
        resourceType: 'DiagnosticReport',
        code: codeableConcept,
        status: fhirR4.DiagnosticReport.StatusEnum.Final,
        subject: this.patientReference
      };
      if (observationReferenceList.length > 0) {
        diagnosticReport.result = observationReferenceList;
      }
      if (diagnosticsFormCollection.diagnosticReportFhirResourceId) {
        diagnosticReport.id = diagnosticsFormCollection.diagnosticReportFhirResourceId;
      }
    }
    return diagnosticReport;
  }

  private getValueFromFormCollection(formCollection: FormCollection, linkId: string) {
    let value = null;
    for (const input of formCollection.data) {
      if (input.linkId.includes(linkId) && formCollection.form.getRawValue()[input.linkId]) {
        value = formCollection.form.getRawValue()[input.linkId];
      }
    }
    return value;
  }

  private processAcceptanceInformation(acceptanceFormCollection: FormCollection): fhirR4.Appointment[] {
    const listOfAppointments: fhirR4.Appointment[] = [];
    // 110 Booking Date
    const bookingDates =
    acceptanceFormCollection.data.filter(item => item.linkId.includes(AppConstants.bookingDateFieldLinkId));

    // We need to create as many Appointments as there are Booking dates
    if (bookingDates?.length > 0) {
      for (let index = 0; index < bookingDates.length; index++) {
        const bookingDate = bookingDates[index];
        const bookingDateValue = acceptanceFormCollection.form.get(bookingDate.linkId).value;
        let appointmentId = null;
        if (acceptanceFormCollection.appointmentFhirResourceIds) {
          appointmentId = acceptanceFormCollection.appointmentFhirResourceIds[index]?.fhirId;
        }
        if (bookingDateValue) {
          const appointment = this.buildIndividualAppointment(bookingDateValue,
            bookingDate.linkId, acceptanceFormCollection, index, appointmentId);
          listOfAppointments.push(appointment);
        }
      }
    }
    return listOfAppointments;
  }

  private getDeletedProcedures(listOfProcedures: fhirR4.Procedure[], offlistingFormCollection: FormCollection): FhirResourceIdMap[] {
    const existingIds = offlistingFormCollection.procedureFhirResourceIds?.map (x => x.fhirId);
    const uiIds = listOfProcedures?.map(x => x.id);
    if (!uiIds || uiIds.length === 0) {
      return offlistingFormCollection.procedureFhirResourceIds;
    }
    const setUi = new Set([...uiIds]);
    if (!existingIds) {
      return [];
    }
    const deletedIds = new Set( offlistingFormCollection.procedureFhirResourceIds.filter(x => !setUi.has(x.fhirId)));
    return Array.from (deletedIds);
  }

  private getDeletedAppointments(listOfAppointments: fhirR4.Appointment[], acceptanceFormCollection: FormCollection): string [] {
    const existingIds = acceptanceFormCollection.appointmentFhirResourceIds?.map (x => x.fhirId);
    const uiIds = listOfAppointments?.map(x => x.id);
    if (!uiIds || uiIds.length === 0) {
      return existingIds;
    }
    const setUi = new Set([...uiIds]);
    if (!existingIds) {
      return [];
    }
    const deletedIds = new Set( existingIds.filter(x => !setUi.has(x)));
    return  Array.from (deletedIds) ;
  } 

  private buildIndividualAppointment(
    bookingDateValue: any,
    linkId: string,
    acceptanceFormCollection: FormCollection,
    index: number,
    fhirResourceId?: string ): fhirR4.Appointment {
    // #3576, bug fix
    const match = RegExp(/\d+$/).exec(linkId);
    const indexOfBookingDate = match ? Number(match[0]) : NaN;
    const cancellationReasonValue = isNaN(indexOfBookingDate)
      ? acceptanceFormCollection.form.get(AppConstants.cancellationsAndBookingsCancellationReasonLinkId).value
      : acceptanceFormCollection.form.get(`${AppConstants.cancellationsAndBookingsCancellationReasonLinkId}-${indexOfBookingDate}`).value;
    // #3560, Accepting the date value without converting it into a date object
    const period: fhirR4.Period = {
      start: bookingDateValue
    };

    const participant: fhirR4.AppointmentParticipant = {
      actor: this.patientReference,
      status: fhirR4.AppointmentParticipant.StatusEnum.Accepted
    };

    const cancellationReasonCodeableConcept =
    this.setCodeableConcept(cancellationReasonValue, AppConstants.cancellationReasonCodeSystem, 'Cancellation Reason');
    const appointment: fhirR4.Appointment = {
      resourceType: 'Appointment',
      requestedPeriod: [period],
      participant: [participant]
    };

    // Start and End date
    // Rule: Only proposed or cancelled appointments can be missing start/end dates
    appointment.start = this.utilService.getStartInstantForGivenDate(bookingDateValue);
    appointment.end = this.utilService.getEndInstantForGivenDate(bookingDateValue);

    if (fhirResourceId) {
      appointment.id = fhirResourceId;
    }

    if (cancellationReasonValue) {
      appointment.cancelationReason = cancellationReasonCodeableConcept;
      appointment.status = fhirR4.Appointment.StatusEnum.Cancelled;
    } else {
      appointment.status = fhirR4.Appointment.StatusEnum.Booked;
    }

     // Identifier 
     appointment.identifier = [];
     const identifier = new fhirR4.Identifier();
     identifier.system =  'https://corhealth-ontario.ca/NamingSystem/appointment-id';
     identifier.value =  index.toString();
     appointment.identifier.push(identifier);

    return appointment;
  }

  private addResourcesToFirstTransactionBundle(resourceList: fhirR4.Resource[]) {
    for (const resource of resourceList) {
      this.addIndividualResourceToFirstTransactionBundle(resource, 'POST');
    }
  }

  private addIndividualResourceToFirstTransactionBundle(resource: fhirR4.Resource, method: string): fhirR4.BundleEntry {
    const bundleEntry: fhirR4.BundleEntry = this.buildBundleEntryFromResource(resource, method);
    if (this.transactionBundle.entry) {
      this.transactionBundle.entry.push(bundleEntry);
    } else {
      this.transactionBundle.entry = [bundleEntry];
    }
    return bundleEntry;
  }

  private buildBundleEntryFromResource(resource: fhirR4.Resource, method: string): fhirR4.BundleEntry {
    const bundleEntryRequest: fhirR4.BundleRequest = {};
    let bundleEntryFullUrl = '';
    bundleEntryRequest.method = method;
    if (method === 'POST') {
      if (resource.resourceType === 'Bundle') {
        bundleEntryRequest.url = environment.resourceProviderURI;
      } else {
        bundleEntryRequest.url = resource.resourceType;
      }
      // Rule: FullUrl must be unique in a bundle
      bundleEntryFullUrl = 'urn:uuid:' + uuidv4();
    } else if (method === 'PUT' || method === 'DELETE') {
      bundleEntryRequest.url = resource.resourceType + '/' + resource.id;
      bundleEntryFullUrl = environment.resourceProviderURI + '/' + resource.resourceType + '/' + resource.id;
    } 
    const bundleEntry: fhirR4.BundleEntry = {
      request: bundleEntryRequest,
      fullUrl: bundleEntryFullUrl
    };
    if (resource) {
      bundleEntry.resource = resource;
    }
    return bundleEntry;
  }

  private buildDeleteBundleEntryFromResourceTypeAndId(resourceType: string, resourceId: string) {
    const bundleEntryRequest: fhirR4.BundleRequest = {};
    bundleEntryRequest.method = 'DELETE';
    bundleEntryRequest.url = resourceType + '/' + resourceId;
    const bundleEntry: fhirR4.BundleEntry = {
      request: bundleEntryRequest
    };
    return bundleEntry;
  }

  // This is called once for each DRP
  // Each DRP will have a single occurence of Offlisting observations
  // Each DRP will have a multiple occurences of Stent observations
  private buildAllOfflistingObservations(
    formCollection: FormCollection,
    formCollectionData: BaseInput<any>[],
    procedureIndex: string): fhirR4.BundleEntry[] {
      const observationsList: fhirR4.Observation[] = [];
      let observationsEntriesList: fhirR4.BundleEntry[] = [];
      const stentDataMap: StentDataMap = {};
      for (const input of formCollectionData) {
        const linkId = input.linkId;
        const fhirResourceId = input.fhirResourceId;
        if (formCollection.form.getRawValue()[linkId]) {
          /**
           * linkIdWithoutIndexes removes the indexes at the end of the linkId to get a proper link id.
           * For example for linkId stent-lesion-location-2-1, linkIdWithoutIndexes will be stent-lesion-location.
           */
          const linkIdWithoutIndexes = linkId.replace(/-[0-9]*[0-9]/g, '');
          let linkIdAndCodeForObservation = ClinicalConstants.procedureObservationList.find(item => linkIdWithoutIndexes === (item.linkId));
          if (linkIdAndCodeForObservation) {
            let observation = this.buildOfflistingObservationWithoutAnswer(linkIdAndCodeForObservation.code, input.label, procedureIndex);
            observation = this.addAnswerToObservation(observation, linkIdAndCodeForObservation, formCollection, linkId);
            observationsList.push(observation);
            if (fhirResourceId) {
              observation.id = fhirResourceId;
              observationsEntriesList.push(this.addIndividualResourceToFirstTransactionBundle(observation, 'PUT'));
            } else {
              observationsEntriesList.push(this.addIndividualResourceToFirstTransactionBundle(observation, 'POST'));
            }
          } else {
            linkIdAndCodeForObservation = ClinicalConstants.stentElements.find(item => linkIdWithoutIndexes === (item.linkId));
            if (linkIdAndCodeForObservation) {
              const stentIndex = this.getIndexOfStentLinkIds(linkId);
              if (stentDataMap[stentIndex]) {
                stentDataMap[stentIndex].push(input);
              } else {
                stentDataMap[stentIndex] = [input];
              }
            }
          }
        }
      }
      // Build stent Observations
      const stentBundleEntries: fhirR4.BundleEntry[] =
      this.processStentComponents(formCollection, stentDataMap);
      if (stentBundleEntries?.length > 0) {
        // Add Stent Observations to the return value
        observationsEntriesList = observationsEntriesList.concat(stentBundleEntries);
      }
      return observationsEntriesList;
    }

    private findFhirIdFromFormCollectionUsingObservationCode(
      formCollection: FormCollection,
      observationCode: string): string {
        let fhirId = null;
        const observations: ObservationResourceIdMap[] = formCollection.observationFhirResourceIds;
        if (observations?.length > 0) {
          const observation = observations.find(item => item.observationCode === observationCode);
          fhirId = observation?.fhirId;
        }
        return fhirId;
      }

    private processStentComponents(
      formCollection: FormCollection,
      stentDataMap: StentDataMap): fhirR4.BundleEntry[] {
      // Build stents and add it to stentMap
      // This map contains the values of the stents with sub index as the key
      // As multiple sections of stents can be added to each Procedure, add stents based on the sub index
      // Example: stent-size-0, stent-size-0-1, stent-size-0-2
      // 0 is the index of the Procedure, 1 & 2 are the sub indices
      const stentBundleEntries: fhirR4.BundleEntry[] = [];
      const stentIndices = Object.keys(stentDataMap);
      for (const stentIndex of stentIndices) {
        const indexForIdentifier = this.buildIndexForStentElements(stentIndex);
        const stentDataOfIndex: BaseInput<any>[] = stentDataMap[stentIndex];
        // Build one Observation for each set of Stent information
        const observation = this.buildOfflistingObservationWithoutAnswer(AppConstants.stentObservationsCodeValue,
          AppConstants.stentObservationsCodeDisplay, indexForIdentifier);
        for (const input of stentDataOfIndex) {
          const linkId = input.linkId;
          const linkIdAndCodeForStentObservation: ICodeAndLinkIdForObservations =
          ClinicalConstants.stentElements.find(item => linkId.includes(item.linkId));
          // Build stent elements
          if (linkIdAndCodeForStentObservation) {
            const answerValue = formCollection.form.getRawValue()[linkId];
            const component: fhirR4.ObservationComponent =
            this.buildStentComponents(linkIdAndCodeForStentObservation.code, answerValue);
            if (observation.component) {
              observation.component.push(component);
            } else {
              observation.component = [component];
            }
            if (input.fhirResourceId) {
              observation.id = input.fhirResourceId;
            }
          }
        }
        if (observation.id) {
          stentBundleEntries.push(this.addIndividualResourceToFirstTransactionBundle(observation, 'PUT'));
        } else {
          stentBundleEntries.push(this.addIndividualResourceToFirstTransactionBundle(observation, 'POST'));
        }
      }
      return stentBundleEntries;
    }

    private buildStentComponents(componentCode: string, answerValue: string): fhirR4.ObservationComponent {
      let retVal: fhirR4.ObservationComponent = null;
      switch (componentCode) {
        case '127':
          // Lesion Location
          const deploymentLocationCodeableConcept: fhirR4.CodeableConcept =
          this.setCodeableConcept('127', AppConstants.stentObservationCodeSystem, 'Lesion Location');
          const deploymentLocationValueCodeableConcept: fhirR4.CodeableConcept =
          this.setCodeableConcept(answerValue, AppConstants.stentDeploymentLocationCodeSystem);
          const deploymentLocationComponent: fhirR4.ObservationComponent = {
            code: deploymentLocationCodeableConcept,
            valueCodeableConcept: deploymentLocationValueCodeableConcept
          };
          retVal = deploymentLocationComponent;
          break;
        case '128':
          // Lesion Type
          const lesionTypeCodeableConcept: fhirR4.CodeableConcept =
          this.setCodeableConcept('128', AppConstants.stentObservationCodeSystem, 'Lesion Type');
          const lesionTypeValueCodeableConcept: fhirR4.CodeableConcept =
          this.setCodeableConcept(answerValue, AppConstants.lesionTypeCodeSystem);
          const lesionTypeComponent: fhirR4.ObservationComponent = {
            code: lesionTypeCodeableConcept,
            valueCodeableConcept: lesionTypeValueCodeableConcept
          };
          retVal = lesionTypeComponent;
          break;


          case '177':
            // Chronic Total Occlusion Result
            const stentChronicTotalCodeableConcept: fhirR4.CodeableConcept =
            this.setCodeableConcept('177', AppConstants.stentObservationCodeSystem, 'Chronic Total Occlusion Result');
            const stentChronicTotalValueCodeableConcept: fhirR4.CodeableConcept =
            this.setCodeableConcept(answerValue, AppConstants.CHRONIC_TOTAL_OCCLUSION_RESULT_CODE_SYSTEM);
            const stentChronicTotalComponent: fhirR4.ObservationComponent = {
              code: stentChronicTotalCodeableConcept,
              valueCodeableConcept: stentChronicTotalValueCodeableConcept
            };
            retVal = stentChronicTotalComponent;
            break;


          case '129':
            // Device Type
            const stentTypeCodeableConcept: fhirR4.CodeableConcept =
            this.setCodeableConcept('129', AppConstants.stentObservationCodeSystem, 'Device Type');
            const stentTypeValueCodeableConcept: fhirR4.CodeableConcept =
            this.setCodeableConcept(answerValue, AppConstants.stentTypeCodeSystem);
            const stentTypeComponent: fhirR4.ObservationComponent = {
              code: stentTypeCodeableConcept,
              valueCodeableConcept: stentTypeValueCodeableConcept
            };
            retVal = stentTypeComponent;
            break;
          case '130':
            // Stent Size
            const stentSizeCodeableConcept: fhirR4.CodeableConcept =
            this.setCodeableConcept('130', AppConstants.stentObservationCodeSystem, 'Stent Size');
            const stentSizeValueQuantity: fhirR4.Quantity = {
              value: Number(answerValue),
              unit: 'mm'
            };
            const stentSizeComponent: fhirR4.ObservationComponent = {
              code: stentSizeCodeableConcept,
              valueQuantity: stentSizeValueQuantity
            };
            retVal = stentSizeComponent;
            break;
          case '131':
            // Stent Length
            const stentLengthCodeableConcept: fhirR4.CodeableConcept =
            this.setCodeableConcept('131', AppConstants.stentObservationCodeSystem, 'Stent Length');
            const stentLengthValue = Number(answerValue);
            const stentLengthComponent: fhirR4.ObservationComponent = {
              code: stentLengthCodeableConcept,
              valueInteger: stentLengthValue
            };
            retVal = stentLengthComponent;
            break;
      }
      return retVal;
    }

    private setObservationAnswer(
      input: BaseInput<any>,
      linkId: string,
      formCollection: FormCollection,
      observation: fhirR4.Observation): fhirR4.Observation {
      if (input.controlType === 'dropdown') {
        const dropDownInput = input as DropdownInput;
        if (dropDownInput.type === 'string' || dropDownInput.type === 'code') {
          const valueString = formCollection.form.getRawValue()[linkId];
          observation.valueString = valueString;
        } else if (dropDownInput.type === 'dynamic') {
          //console.log('Dynamic input type');
        } else {
          //console.log('unaccounted input type');
        }
      } else if (input.controlType === 'datepicker' || input.controlType === 'dateTimePicker') {
        const valueDate = formCollection.form.getRawValue()[linkId];
        observation.valueDateTime = valueDate;
      } else if (input.controlType === 'checkbox') {
        const valueBoolean = formCollection.form.getRawValue()[linkId];
        observation.valueBoolean = valueBoolean;
      } else {
        const value = formCollection.form.getRawValue()[linkId];
        observation.valueString = value;
      }
      return observation;
    }

  // Builds Observation for all sections except Offlisting
  private buildObservationsForGivenSection(
    formCollection: FormCollection,
    formCollectionData: BaseInput<any>[],
    linkIdsForObservationsList: ICodeAndLinkIdForObservations[]): fhirR4.BundleEntry[] {
    const observationsList: fhirR4.Observation[] = [];
    const observationsEntriesList: fhirR4.BundleEntry[] = [];
    let dartsIndex = 0;
    for (const input of formCollectionData) {
      const linkId = input.linkId;
      // #3017, wait1 or wait2 value '0' case
      if (formCollection.form.getRawValue()[linkId] || this.utilService.isNumber(formCollection.form.getRawValue()[linkId]) ||
          input.dataMissingReason) {
        let linkIdAndCodeForObservation: ICodeAndLinkIdForObservations;
        let repeatableElement = false;
        // #3489
        if (linkId.includes('grade') || linkId.includes('dart-days')) {
          linkIdAndCodeForObservation = linkIdsForObservationsList.find(item => linkId === (item.linkId));
        } else {
          linkIdAndCodeForObservation = linkIdsForObservationsList.find(item => linkId.includes(item.linkId));
        }
        if (linkIdAndCodeForObservation) {
          // Get FHIR Id
          let fhirResourceId = this.findFhirIdFromFormCollectionUsingObservationCode(
            formCollection, linkIdAndCodeForObservation.code);
          // Build Observation
          let observation = this.buildObservation(linkIdAndCodeForObservation, input.label);
          // DARTs
          if (
            linkIdAndCodeForObservation.code === AppConstants.DART_FROM_OBSERVATION_CODE ||
            linkIdAndCodeForObservation.code === AppConstants.DART_TO_OBSERVATION_CODE) {
              repeatableElement = true;
            if (linkIdAndCodeForObservation.code === AppConstants.DART_FROM_OBSERVATION_CODE) {
              // Create a new Obs with the value DART from and map it to period.start
              const valueDate = formCollection.form.getRawValue()[linkId];
              const period: fhirR4.Period = {
                start: valueDate
              };
              observation.valuePeriod = period;
              dartsIndex++;
            } else {
              // Get all DART obs and check for period.end, if a period.end is empty, map the DART to date to it
              const valueDate = formCollection.form.getRawValue()[linkId];
              observationsList.forEach(dartObservation => {
                const code = this.getObservationCodeFromCodeableConcept(dartObservation.code);
                if (code === AppConstants.DART_FROM_OBSERVATION_CODE) {
                  if (dartObservation.valuePeriod && !dartObservation.valuePeriod.end) {
                    dartObservation.valuePeriod.end = valueDate;
                  }
                }
              });
            }
          } else if (
            linkIdAndCodeForObservation.code === AppConstants.RACE_OBSERVATION_CODE ||
            linkIdAndCodeForObservation.code === AppConstants.DELAY_REASON_OBSERVATION_CODE) {
            // Race
            const raceValue = formCollection.form.getRawValue()[linkId];
            repeatableElement = true;
            if (raceValue) {
              observation = this.addAnswerToObservation(observation, linkIdAndCodeForObservation, formCollection, linkId, input.label);
            } else {
              //console.log('Unaccounted value for Race');
            }
          } else if (linkIdAndCodeForObservation.code === AppConstants.PATIENT_PRESENTATION_OBSERVATION_CODE) {
            // #3342 - Add Non-Percutaneous Coronary Intervention Capable Hospital to the same Observation as Patient Presentation
            observation = this.addAnswerToObservation(observation, linkIdAndCodeForObservation, formCollection, linkId);
            const nonPercutaneousHospitalId = formCollection.form.getRawValue()[AppConstants.nonPercutaneousHospitalLinkId];
            if (nonPercutaneousHospitalId) {
              const valueReference: fhirR4.Reference = new fhirR4.Reference();
              valueReference.reference = nonPercutaneousHospitalId;
              observation.performer = [valueReference];
            }
          } else {
            if (input.controlType === 'dropdown') {
              const dropDownInput = input as DropdownInput;
              if (dropDownInput.type === 'string' || dropDownInput.type === 'code') {
                observation = this.addAnswerToObservation(observation, linkIdAndCodeForObservation, formCollection, linkId);
              } else if (dropDownInput.type === 'dynamic') {
                //console.log('Dynamic input type');
              } else {
                //console.log('unaccounted input type');
              }
            } else if (input.controlType === 'datepicker' || input.controlType === 'dateTimePicker') {
              if (input.dataMissingReason) {
                const dataMissingReason = this.clinicalAdapterService.convertEntryValuesToClinicalDataAbsentReason(input.dataMissingReason);
                const codeableConcept: fhirR4.CodeableConcept = this.setCodeableConcept(
                  dataMissingReason,
                  AppConstants.dataAbsentReasonSystem);
                observation.dataAbsentReason = codeableConcept;
              } else {
                const valueDate = formCollection.form.getRawValue()[linkId];
                observation.valueDateTime = valueDate;
              }
            } else if (input.controlType === 'checkbox') {
              const valueBoolean = formCollection.form.getRawValue()[linkId];
              observation.valueBoolean = valueBoolean;
            } else {
              observation = this.addAnswerToObservation(observation, linkIdAndCodeForObservation, formCollection, linkId);
            }
          }
          // #3017
          if (
            observation.valueString ||
            this.utilService.isNumber(observation.valueString) ||
            ((observation.valueBoolean || observation.valueBoolean === false)
            && !input.linkId.startsWith('race') && !input.linkId.startsWith('delay-reason')) ||
            observation.valueDateTime ||
            observation.valuePeriod ||
            observation.dataAbsentReason ||
            // wait 1 and 2 are actual integer, weight and height are string stored in valueInteger
            Number.isInteger(observation.valueInteger) || this.utilService.isNumber(observation.valueInteger?.toString()) ||
            observation.valueQuantity ||
            observation.valueCodeableConcept) {
            const isObservationAWaitTimeMetric =
            ClinicalConstants.waitTimesObservationsList.find(item => item.linkId === linkIdAndCodeForObservation.linkId);
            if (isObservationAWaitTimeMetric) {
              // If the value is 'Not applicable' for a wait time metric, then skip creating an Observation for it
              if (observation.valueString !== 'Not Applicable') {
                observationsList.push(observation);
              }
            } else {
              observationsList.push(observation);
            }
            if (repeatableElement) {
              fhirResourceId = input.fhirResourceId;
            }
            // Add observations to first Transaction
            if (fhirResourceId) {
              observation.id = fhirResourceId;
              observationsEntriesList.push(this.addIndividualResourceToFirstTransactionBundle(observation, 'PUT'));
            } else {
              observationsEntriesList.push(this.addIndividualResourceToFirstTransactionBundle(observation, 'POST'));
            }
          }
        }
      }
    }

    // Prepare list of Observations to be deleted
    // Only check for Observations to be deleted if an Entry is being edited. Skip checking if an Entry is being created for the 1st time.
    // formCollection.observationFhirResourceIds is populated when editing an Entry
    if (formCollection?.observationFhirResourceIds?.length > 0) {
      this.prepareListOfObservationsToBeDeleted(linkIdsForObservationsList, formCollection, observationsList);
    }

    return observationsEntriesList;
  }

  private prepareListOfObservationsToBeDeleted(
    linkIdsForObservationsList: ICodeAndLinkIdForObservations[],
    formCollection: FormCollection,
    observationsList: fhirR4.Observation[]) {
    if (observationsList.length > 0) {
      // Process all sections other than Offlisting
      if (!linkIdsForObservationsList.find(item => item.code === AppConstants.REMOVAL_REASON_OBSERVATION_CODE)) {
        formCollection?.observationFhirResourceIds?.forEach(observationFhirId => {
          let match = false;
          observationsList.forEach(observation => {
            if (observation.id && observation.id === observationFhirId.fhirId) {
              match = true;
            }
          });
          if (!match && !this.listOfObservationsToBeDeleted.includes(observationFhirId.fhirId)) {
            this.listOfObservationsToBeDeleted.push(observationFhirId.fhirId);
          }
        });
      }
    } else {
      // When an entire section is deleted, submit all formCollection.observationFhirResourceIds to de deleted.
      // This condition also applies to a single offlisting observation, 113 - Removal Reason
      // When the entire Offlisting section is deleted, including the Removal date and reason, the removal reason is submitted for delete here
      formCollection.observationFhirResourceIds.forEach(observationFhirId => this.listOfObservationsToBeDeleted.push(observationFhirId.fhirId));
    }
  }

  private addAnswerToObservation(
    observation: fhirR4.Observation,
    linkIdAndCodeForObservation: ICodeAndLinkIdForObservations,
    formCollection: FormCollection,
    linkId: string,
    inputLabel?: string): fhirR4.Observation {
      let value: string | number | boolean | Date = formCollection.form.getRawValue()[linkId];
      if (
        linkIdAndCodeForObservation.code === AppConstants.RACE_OBSERVATION_CODE ||
        linkIdAndCodeForObservation.code === AppConstants.DELAY_REASON_OBSERVATION_CODE) {
          value = inputLabel;
        }
      if (
        value === AppConstants.UNKNOWN_ENTRY_VALUE_TEXT ||
        value === AppConstants.UNKNOWN_ENTRY_VALUE_CODE ||
        value === AppConstants.NOT_APPLICABLE_ENTRY_VALUE ||
        value === AppConstants.NOT_APPLICABLE_DISPLAY_VALUE) {
        const dataAbsentReason =
        this.clinicalAdapterService.convertEntryValuesToClinicalDataAbsentReason(value as string);
        const dataAbsentCodeableConcept: fhirR4.CodeableConcept =
        this.setCodeableConcept(dataAbsentReason, AppConstants.DATA_ABSENT_REASON_CODE_SYSTEM);
        observation.dataAbsentReason = dataAbsentCodeableConcept;
      } else {
        switch (linkIdAndCodeForObservation.answerType) {
          case AppConstants.VALUE_TYPE_BOOLEAN:
            const valueBoolean = this.clinicalAdapterService.convertEntryValuesToBoolean(value as string);
            observation.valueBoolean = valueBoolean;
            break;
          case AppConstants.VALUE_TYPE_CODEABLE_CONCEPT:
            value =
            this.clinicalAdapterService.getClinicalValueFromEntryValue(linkIdAndCodeForObservation.code, value as string);
            const valueCodeableConcept: fhirR4.CodeableConcept =
            this.setCodeableConcept(value, linkIdAndCodeForObservation.answerCodeSystem, null);
            observation.valueCodeableConcept = valueCodeableConcept;
            break;
          case AppConstants.VALUE_TYPE_DATETIME:
            observation.valueDateTime = value as string;
            break;
          case AppConstants.VALUE_TYPE_INTEGER:
            const valueInteger: number = +value;
            observation.valueInteger = valueInteger;
            break;
          case AppConstants.VALUE_TYPE_QUANTITY:
            const quantity: fhirR4.Quantity = new fhirR4.Quantity();
            quantity.value = +value;
            observation.valueQuantity = quantity;
            break;
          case AppConstants.VALUE_TYPE_STRING:
            // #3267 - Patient Postal Code
            if (this.patientPostalCodeOriginalValue != null &&
              linkIdAndCodeForObservation.code === AppConstants.PATIENT_REFERRAL_POSTAL_CODE_OBSERVATION_CODE) {
              observation.valueString = this.patientPostalCodeOriginalValue;
            } else {
              observation.valueString = value.toString();
            }
            break;
        }
      }
      return observation;
    }

  private buildOfflistingObservationWithoutAnswer(codeValue: string, displayValue: string, index: string): fhirR4.Observation {
    const coding: fhirR4.Coding = {
      system: AppConstants.observationCodeSystemUri,
      code: codeValue,
      display: displayValue
    };
    const codeableConcept: fhirR4.CodeableConcept = {
      coding: [coding]
    };
    const observation: fhirR4.Observation = {
      resourceType: 'Observation',
      code: codeableConcept,
      status: fhirR4.Observation.StatusEnum.Final
    };
    observation.identifier =
    this.addIdentifierValueForProcedureElements(index, AppConstants.OBSERVATION_ORDER_IDENTIFIER_SYSTEM, observation.identifier);
    return observation;
  }

  private buildObservation(linkIdAndCodeForObservation: ICodeAndLinkIdForObservations, displayValue: string): fhirR4.Observation {
    const codeSystem = linkIdAndCodeForObservation.observationCodeSystem ?
    linkIdAndCodeForObservation.observationCodeSystem : AppConstants.observationCodeSystemUri;
    // Wait time Observations have a different Code system
    const coding: fhirR4.Coding = {
      system: codeSystem,
      code: linkIdAndCodeForObservation.code,
      display: displayValue
    };
    const codeableConcept: fhirR4.CodeableConcept = {
      coding: [coding]
    };
    const observation: fhirR4.Observation = {
      resourceType: 'Observation',
      code: codeableConcept,
      status: fhirR4.Observation.StatusEnum.Final
    };
    return observation;
  }

  private processOfflistingInformation(offlistingFormCollection: FormCollection): fhirR4.Procedure[] {
    // Diagnostic Report for Procedure, and Procedure need to be built for every single instance of the subsection being added
    // The input for these 2 should be a filtered formCollection, with each one being separated based on index
    // Filtering formCollection - get last char and check if its a number
    const offlistingInputDataByIndex = [];
    for (const baseInput of offlistingFormCollection.data) {
      if (offlistingFormCollection.form.getRawValue()[baseInput.linkId]) {
        const linkId = baseInput.linkId;
        // Removal date and Removal reason do not below to any procedure
        if (linkId === AppConstants.removalDateFieldLinkId || linkId === AppConstants. removalReasonLinkId) {
          continue;
        }
        // Instead of last character, look for first occurence of a number
        // This is to account for items that have linkIds with 2 numbers, ex: stent-deployment-location-0-1
        const index = this.getIndexOfProcedureLinkIds(linkId);
        if (offlistingInputDataByIndex[index]) {
          offlistingInputDataByIndex[index].push(baseInput);
        } else {
          offlistingInputDataByIndex[index] = [baseInput];
        }
      }
    }
    const retVal: fhirR4.Procedure[] = [];
    let procObservations: fhirR4.Observation[] = [];
    for (let i = 0; i < offlistingInputDataByIndex.length; i++) {
      const {offlistingObservationBundleEntries, procedureDiagnosticReportBundleEntry} = this.buildProcedureDiagnosticReport(offlistingFormCollection, offlistingInputDataByIndex[i], i.toString());
      const procedure: fhirR4.Procedure = this.buildProcedure(offlistingFormCollection, i.toString(), procedureDiagnosticReportBundleEntry);
      if (procedure) {
        retVal.push(procedure);
      }
      if (offlistingObservationBundleEntries?.length > 0) {
        offlistingObservationBundleEntries.forEach(offlistingObservationBundleEntry => procObservations.push(offlistingObservationBundleEntry.resource as fhirR4.Observation));
      }
    }
    if (offlistingFormCollection.procedureObservationFhirResourceIds) {
      offlistingFormCollection.procedureObservationFhirResourceIds?.forEach(observationMap => {
        if (!procObservations || !procObservations.find(x => x.id  === observationMap.fhirId )) {
          this.deletedOfflistingObservationList.push(observationMap.fhirId);
        }
      });
    }
    return retVal;
  }

  // LinkIds of Procedures have an index and a sub index, example: stent-deployment-location-1-2
  // Index is 1 and sub index is 2
  // All procedure items of the same index are added to the same DiagnosticReportProcedure
  // Fetch the index by passing in the linkId
  private getIndexOfProcedureLinkIds(linkId: string): number {
    const char = linkId.split('').find(individualChar => !isNaN(Number(individualChar)));
    let index = Number(char);
    if (!index) {
      // If index is not a number, then set index to 0
      // This condition is for all items whose linkId's do not have a number
      index = 0;
    }
    return index;
  }

  // Sent link Id like this: first procedure: first stent, stent-type-0; second stent: stent-type-0-1
  // second procedure: first stent: stent-type-1; second stent: stent-type-1-1
  private getIndexOfStentLinkIds(linkId: string): string {
    const parsedArray = linkId.split('-');
    const itemSuffix = parsedArray[parsedArray.length - 1];
    const stentSuffix = parsedArray[parsedArray.length - 2];
    // linkId like stent-type-0-1, setent-type-1-1, return '0-1', '1-1'
    if (!isNaN(+stentSuffix)) {
      return stentSuffix + '-' + itemSuffix;
    } else if (!isNaN (+itemSuffix)) {
      // linkId like stent-type-0, stent-type-1
      return itemSuffix;
    } else {
        return '0'; // shouldn't have stent link Id without any number post fix, like stent-type
    }
  }

  private buildProcedure(offlistingFormCollection: FormCollection, index: string, procedureDiagnosticReportBundleEntry: fhirR4.BundleEntry): fhirR4.Procedure {
    let createProcedure = false;
    const procedureIndex = this.buildIndexForProcedureElements(index);
    let linkIdSuffix = '';
    if (index !== '0') {
      linkIdSuffix = '-' + index;
    }
    let fhirResourceId = null;
    const offlistingForm: FormGroup = offlistingFormCollection.form;
    if (offlistingFormCollection.procedureFhirResourceIds) {
      const fhirIdMap: FhirResourceIdMap =
      offlistingFormCollection.procedureFhirResourceIds.find(item => item.index === procedureIndex);
      fhirResourceId = fhirIdMap?.fhirId;
    }

    // #3599 - Replace meta tag indices for Procedure elements
    const identifier: fhirR4.Identifier = this.buildIdentifierForProcedureElements(index, AppConstants.PROCEDURE_ORDER_IDENTIFIER_SYSTEM);

    // 113. Removal Reason - this has been moved to SR
    // const removalReason = offlistingForm.get(AppConstants.removalReasonFieldLinkId + linkIdSuffix).value;
    // const removalReasonCodeableConcept = this.setCodeableConcept(removalReason, AppConstants.observationCodeSystemUri, 'Removal Reason');

    // 114. Procedure
    const procedureValue = offlistingForm.get(AppConstants.allProceduresLinkId + linkIdSuffix).value;
    const procedureValueCodeableConcept = this.setCodeableConcept(procedureValue, AppConstants.procedureCodeSystem, 'Procedure');
    if (procedureValueCodeableConcept) {
      createProcedure = true;
    }

    const diagnosticReportReference = this.buildReferenceForSingleBundleEntry(procedureDiagnosticReportBundleEntry, 'DiagnosticReport');

    let procedure: fhirR4.Procedure = {
      resourceType: 'Procedure',
      subject: this.patientReference,
      // statusReason: removalReasonCodeableConcept,
      code: procedureValueCodeableConcept,
      identifier: [identifier],
      report: [diagnosticReportReference]
    };

    // 116. Access Site
    const accessSite = offlistingForm.get(AppConstants.accessSiteLinkId + linkIdSuffix)?.value;
    if (accessSite) {
      createProcedure = true;
      const accessSiteCodeableConcept = this.setCodeableConcept(accessSite, AppConstants.accessSiteCodeSystem, 'Access Site');
      procedure.bodySite = [accessSiteCodeableConcept];
    }

    if (fhirResourceId) {
      procedure.id = fhirResourceId;
    }

    // 157. Treating Health Care Professional
    const treatingHCP = offlistingForm.get(AppConstants.OffListingProcedureHCPLinkId + linkIdSuffix).value;
    if (treatingHCP) {
      createProcedure = true;
      const treatingHCPFunction = this.setCodeableConcept(AppConstants.treatingHealthCareProfessionalCode,
        AppConstants.performerCodeSystem, AppConstants.treatingHealthCareProfessionalLabel);
      const treatingHCPPerformer: fhirR4.ProcedurePerformer = {
        actor: {
          reference: treatingHCP
        }
      };
      treatingHCPPerformer.function = treatingHCPFunction;
      procedure.performer = [treatingHCPPerformer];
    }

    // 158. Second Treating Health Care Professional
    const secondTreatingHCP = offlistingForm.get(AppConstants.OffListingProcedureSecondHCPLinkId + linkIdSuffix).value;
    if (secondTreatingHCP) {
      createProcedure = true;
      const secondTreatingHCPFunction = this.setCodeableConcept(AppConstants.secondTreatingHealthCareProfessionalCode,
        AppConstants.performerCodeSystem, AppConstants.secondTreatingHealthCareProfessionalLabel);
      const secondTreatingHCPPerformer: fhirR4.ProcedurePerformer = {
        actor: {
          reference: secondTreatingHCP
        }
      };
      secondTreatingHCPPerformer.function = secondTreatingHCPFunction;
      if (procedure.performer) {
        procedure.performer.push(secondTreatingHCPPerformer);
      } else {
        procedure.performer = [secondTreatingHCPPerformer];
      }
    }

    // 115 Procedure status
    const procedureStatus = offlistingForm.get(AppConstants.procedureStatusLinkId + linkIdSuffix).value;
    if (procedureStatus === 'C') {
      procedure.status = AppConstants.procedureFHIRStatusCompleted;
    } else if (procedureStatus === 'N') {
      procedure.status = AppConstants.procedureFHIRStatusNotDone;
    }

    if (!createProcedure) {
      procedure = null;
    }

    return procedure;
  }

  private buildProcedureDiagnosticReport(
    offlistingFormCollection: FormCollection,
    offlistingFormCollectionData: BaseInput<any>[],
    index: string): {offlistingObservationBundleEntries: fhirR4.BundleEntry[], procedureDiagnosticReportBundleEntry: fhirR4.BundleEntry} {
      let procedureDiagnosticReportBundleEntry: fhirR4.BundleEntry;

    const procedureIndex = this.buildIndexForProcedureElements(index);
    let fhirResourceId = null;
    if (offlistingFormCollection.diagnosticReportProcedureFhirResourceIds) {
      const fhirIdMap: FhirResourceIdMap =
      offlistingFormCollection.diagnosticReportProcedureFhirResourceIds.find(item => item.index === procedureIndex);
      fhirResourceId = fhirIdMap?.fhirId;
    }
    // Observations
    const offlistingObservationBundleEntries: fhirR4.BundleEntry[] =
    this.buildAllOfflistingObservations(offlistingFormCollection, offlistingFormCollectionData, procedureIndex);

    // Build DRP only if there is atleast 1 Observation
    if (offlistingObservationBundleEntries?.length > 0) {

      // #3599 - Replace meta tag indices for Procedure elements
      const identifier: fhirR4.Identifier =
      this.buildIdentifierForProcedureElements(index, AppConstants.DIAGNOSTIC_REPORT_PROCEDURE_ORDER_IDENTIFIER_SYSTEM);

      const codeableConcept: fhirR4.CodeSystemConcept = this.setCodeableConcept(
        AppConstants.codeValueForProcedureDiagnosticReport,
        AppConstants.loincCodeSystemUri,
        AppConstants.codeDisplayForProcedureDiagnosticReport);
      const procedureDiagnosticReport: fhirR4.DiagnosticReport = {
        resourceType: 'DiagnosticReport',
        code: codeableConcept,
        status: fhirR4.DiagnosticReport.StatusEnum.Final,
        identifier: [identifier]
      };
      const observationReferenceList = this.buildReferenceListFromBundleEntries(offlistingObservationBundleEntries, 'Observation');
      if (observationReferenceList.length > 0) {
        procedureDiagnosticReport.result = observationReferenceList;
      }

      if (fhirResourceId) {
        procedureDiagnosticReport.id = fhirResourceId;
        procedureDiagnosticReportBundleEntry = this.addIndividualResourceToFirstTransactionBundle(procedureDiagnosticReport, 'PUT');
      } else {
        procedureDiagnosticReportBundleEntry = this.addIndividualResourceToFirstTransactionBundle(procedureDiagnosticReport, 'POST');
      }
    } else if (fhirResourceId) {
      // No any observation data, should delete the diagnostic report itself
      const oneMap: FhirResourceIdMap = {fhirId: fhirResourceId, index: procedureIndex};
      this.deletedOfflistingDiagnosticReportList.push(oneMap);
    }
    return {offlistingObservationBundleEntries, procedureDiagnosticReportBundleEntry};
  }

  private setCodeableConcept(value: any, system?: string, display?: string): fhirR4.CodeableConcept {
    let codeableConcept: fhirR4.CodeableConcept = null;
    if (value) {
      const valueAsString = value.toString();
      const coding: fhirR4.Coding = {
        code: valueAsString
      };
      if (system) {
        coding.system = system;
      }
      if (display) {
        coding.display = display;
      }
      codeableConcept = {
        coding: [coding],
        text: valueAsString
      };
    }
    return codeableConcept;
  }

  private getCodeFromCodeableConceptArray(codeableConceptArray: fhirR4.CodeableConcept[], system: string): fhirR4.Code {
    let code = null;
    if (codeableConceptArray) {
      for (const codeableConcept of codeableConceptArray) {
        for (const coding of codeableConcept.coding) {
          if (coding.system === system) {
            code = coding.code;
          }
        }
      }
    }
    return code;
  }

  getObservationCodeFromCodeableConcept(codeableConcept: fhirR4.CodeableConcept): fhirR4.Code {
    let code = null;
    if (codeableConcept?.coding) {
      for (const coding of codeableConcept.coding) {
        if (coding.system === AppConstants.observationCodeSystemUri ||
          coding.system === AppConstants.WAIT_TIMES_AND_METRICS_OBSERVATIONS_CODE_SYSTEM ||
          coding.system === AppConstants.stentObservationCodeSystem) {
          code = coding.code;
        }
      }
    }
    return code;
  }

  getCodeFromCodeableConcept(codeableConcept: fhirR4.CodeableConcept, system: string): fhirR4.Code {
    let code = null;
    if (codeableConcept?.coding) {
      for (const coding of codeableConcept.coding) {
        if (coding.system === system) {
          code = coding.code;
        }
      }
    }
    return code;
  }

  getCodeFromCodeableConceptWithoutSystem(codeableConcept: fhirR4.CodeableConcept): fhirR4.Code {
    let code = null;
    if (codeableConcept?.coding) {
      for (const coding of codeableConcept.coding) {
        code = coding.code;
      }
    }
    return code;
  }

  private async handleTransactions(
    listOfAppointments: fhirR4.Appointment[],
    diagnosticReport: fhirR4.DiagnosticReport,
    listOfProcedures: fhirR4.Procedure[],
    deletedAppointments: string[],
    deletedProcedures: FhirResourceIdMap[],
    serviceRequestReference: fhirR4.Reference,
    originDiagnosticReportResourceId?: string
    ): Promise<fhirR4.Bundle> {
    // Fetch details from first transaction
    const serviceRequestEntry = this.transactionBundle.entry.find(item => item.resource.resourceType === 'ServiceRequest');
    const serviceRequest: fhirR4.ServiceRequest = serviceRequestEntry.resource as fhirR4.ServiceRequest;

    // Set Entry ID
    // Entry ID is the identifier for migrated entries
    // Entry ID is the FHIR resource ID for entries created in DCIS
    if (serviceRequest.identifier) {
      const identifierValue =
      this.utilService.getIdentifierValueForGivenSystemUri(serviceRequest.identifier, AppConstants.ENTRY_IDENTIFIER_SYSTEM);
      if (identifierValue) {
        this.entryId = identifierValue;
      } else {
        this.entryId = serviceRequest.id;
      }
    } else {
      this.entryId = serviceRequest.id;
    }

    // Generate Entry Identifier
    // const identifierValue = this.utilService.generateUniqueId(serviceRequestEntry.resource.id);
    // const identifier: fhirR4.Identifier = {
    //   value: identifierValue
    // };
    // serviceRequest.identifier = [identifier];

    if (listOfProcedures?.length > 0) {
      this.buildOfflistingBundleEntries(listOfProcedures, serviceRequestReference);
    }

    if (diagnosticReport) {
      diagnosticReport.basedOn = [serviceRequestReference];
      if (diagnosticReport.id) {
        this.transactionBundle.entry.push(this.buildBundleEntryFromResource(diagnosticReport, 'PUT'));
      } else {
        this.transactionBundle.entry.push(this.buildBundleEntryFromResource(diagnosticReport, 'POST'));
      }
    }

    if (listOfAppointments?.length > 0) {
      // Add SR reference to Appointment
      for (const appointment of listOfAppointments) {
        appointment.basedOn = [serviceRequestReference];
        if (appointment.id) {
          this.transactionBundle.entry.push(this.buildBundleEntryFromResource(appointment, 'PUT'));
        } else {
          this.transactionBundle.entry.push(this.buildBundleEntryFromResource(appointment, 'POST'));
        }
      }
    }
    if (deletedAppointments?.length > 0) {
      for (const fhirId of deletedAppointments) {
        const appointment = new fhirR4.Appointment();
        appointment.id = fhirId;
        appointment.resourceType = 'Appointment';
        this.transactionBundle.entry.push(this.buildBundleEntryFromResource(appointment, 'DELETE'));
      }
    }


    if (deletedProcedures?.length > 0) {
      for (const oneMap of deletedProcedures) {
        const procedure = new fhirR4.Procedure();
        procedure.id = oneMap.fhirId;
        procedure.resourceType = 'Procedure';
        this.transactionBundle.entry.push(this.buildBundleEntryFromResource(procedure, 'DELETE'));
      }
    }

    if (this.deletedOfflistingDiagnosticReportList.length > 0) {
      for (const oneMap of this.deletedOfflistingDiagnosticReportList) { 
          const report = new fhirR4.DiagnosticReport();
          report.id = oneMap.fhirId;
          report.resourceType = 'DiagnosticReport';
          this.transactionBundle.entry.push(this.buildBundleEntryFromResource(report, 'DELETE'));
      }
    }

    // Put in front of deleted procedures
    if (this.deletedOfflistingObservationList?.length > 0) {
      for (const fhirId of this.deletedOfflistingObservationList) 
      {
          const observation = new fhirR4.Observation();
          observation.id = fhirId;
          observation.resourceType = 'Observation';
          this.transactionBundle.entry.push(this.buildBundleEntryFromResource(observation, 'DELETE'));
      }
    }

    if (this.listOfObservationsToBeDeleted?.length > 0) {
      for (const fhirId of this.listOfObservationsToBeDeleted) {
          const observation = new fhirR4.Observation();
          observation.id = fhirId;
          observation.resourceType = 'Observation';
          this.transactionBundle.entry.push(this.buildBundleEntryFromResource(observation, 'DELETE'));
      }
    } 

    if (!diagnosticReport && originDiagnosticReportResourceId) {
      // delete original diagnostic report
      this.transactionBundle.entry.push(
        this.buildDeleteBundleEntryFromResourceTypeAndId('DiagnosticReport', originDiagnosticReportResourceId));
    }

    this.initialSupportingInfoList?.forEach( reference => {
      const id = this.utilService.getIdFromReference(reference.reference);
      if (!serviceRequest.supportingInfo ||
        !serviceRequest.supportingInfo?.find (x => this.utilService.getIdFromReference(x.reference) === id)) {
        if (reference.reference.startsWith('Observation')) {
          const observation = new fhirR4.Observation();
          observation.id = id;
          observation.resourceType = 'Observation';
          this.transactionBundle.entry.push(this.buildBundleEntryFromResource(observation, 'DELETE'));
        }
      }
    });

    // //Fixed issue for duplicate delete request - 4502, 4504 
    // const totalEntries = this.transactionBundle.entry;
    // this.transactionBundle.entry = [];
    // this.transactionBundle.entry = totalEntries.filter((value, index, self) =>
    //   index === self.findIndex((t) => (
    //     t.resource.id === value.resource.id
    //     )
    //   )
    // );
    // console.log(JSON.stringify(secondTransactionBundle));
    // const finalBundle: fhirR4.Bundle = {
    //   resourceType: 'Bundle',
    //   entry: [],
    //   type: fhirR4.Bundle.TypeEnum.Transaction
    // };
    // finalBundle.entry.push(this.buildBundleEntryFromResource(this.firstTransactionBundle, 'POST'));
    // finalBundle.entry.push(this.buildBundleEntryFromResource(secondTransactionBundle, 'POST'));

    // this.firstTransactionBundle.entry.forEach(item => finalBundle.entry.push(item));
    // secondTransactionBundle.entry.forEach(item => finalBundle.entry.push(item));
    // console.log(JSON.stringify(finalBundle));
    //console.log(JSON.stringify(this.transactionBundle));
    const finalBundleResponse: fhirR4.Bundle = await this.httpService.postBundleResource(this.transactionBundle) as fhirR4.Bundle;
    //console.log(JSON.stringify(finalBundleResponse));
    return finalBundleResponse;

  }

  private buildOfflistingBundleEntries(
    listOfProcedures: fhirR4.Procedure[],
    serviceRequestReference: fhirR4.Reference) {
    const offlistingBundleEntries: fhirR4.BundleEntry[] = [];
    for (const procedure of listOfProcedures) {
      let procedureIndex =
      this.utilService.getIdentifierValueForGivenSystemUri(procedure.identifier, AppConstants.PROCEDURE_ORDER_IDENTIFIER_SYSTEM) as string;
      procedure.identifier =
        this.addIdentifierValueForProcedureElements(procedureIndex, AppConstants.PROCEDURE_ORDER_IDENTIFIER_SYSTEM, procedure.identifier);
      if (serviceRequestReference) {
        procedure.basedOn = [serviceRequestReference];
      }
      if (procedure.id) {
        this.transactionBundle.entry.push(this.buildBundleEntryFromResource(procedure, 'PUT'));
      } else {
        this.transactionBundle.entry.push(this.buildBundleEntryFromResource(procedure, 'POST'));
      }
    }
  }

  // ServiceRequest and Organization
  // processServiceInformation(formCollection: FormCollectionSet) {}

  // ServiceRequest, Observation, Organization and Practitioner
  // processReferralInformation(formCollection: FormCollectionSet) {}

  // We need a new Interface that has a collection of all Clinical resources, and that will be the input of this method
  // Output will be the Form Collection
  buildPatientEntryInformation(bundle: fhirR4.Bundle, formCollection: FormCollectionSet): IPatientEntryData {
    // Build IPatientEntryData

    formCollection.service.observationFhirResourceIds = [];
    formCollection.referral.observationFhirResourceIds = [];
    formCollection.diagnostics.observationFhirResourceIds = [];
    formCollection.acceptance.observationFhirResourceIds = [];
    formCollection.offlisting.observationFhirResourceIds = [];
    formCollection.waitTime.observationFhirResourceIds = [];

    const entryInformation: IPatientEntryData = {};
    let serviceRequest: fhirR4.ServiceRequest = null;
    let diagnosticReport: fhirR4.DiagnosticReport = null;
    const listOfAppointments: fhirR4.Appointment[] = [];
    const proceduresMap = {};
    const diagnosticReportProcedureMap = {};
    entryInformation.offlisting = [];

    formCollection.offlisting.procedureFhirResourceIds = [];
    formCollection.offlisting.diagnosticReportProcedureFhirResourceIds = [];

    // const serviceRequest =
    // bundle.entry.find(bundleEntry => bundleEntry.resource.resourceType === 'ServiceRequest').resource as fhirR4.ServiceRequest;
    bundle.entry.forEach((bundleEntry: fhirR4.BundleEntry) => {
      const resource = bundleEntry.resource;
      switch (resource.resourceType) {
        case 'Observation':
          const observation = resource as fhirR4.Observation;
          this.observationsList.push(observation);
          break;
        case 'ServiceRequest':
          serviceRequest = resource as fhirR4.ServiceRequest;
          if (serviceRequest.identifier) {
            const identifier =
            this.utilService.getIdentifierValueForGivenSystemUri(serviceRequest.identifier, AppConstants.ENTRY_IDENTIFIER_SYSTEM);
            if (identifier) {
              this.entryId = identifier;
            } else {
              this.entryId = serviceRequest.id;
            }
          } else {
            this.entryId = serviceRequest.id;
          }
          break;
        case 'DiagnosticReport':
          const diagnosticResource = resource as fhirR4.DiagnosticReport;
          const diagnosticCode = this.getCodeFromCodeableConcept(diagnosticResource.code, AppConstants.loincCodeSystemUri);
          if (diagnosticCode === AppConstants.codeValueForDiagnosticReport) {
            diagnosticReport = diagnosticResource;
          }
          if (diagnosticCode === AppConstants.codeValueForProcedureDiagnosticReport) {
            const diagnosticReportProcedureIndex =
            this.utilService.getIdentifierValueForGivenSystemUri(diagnosticResource.identifier,
              AppConstants.DIAGNOSTIC_REPORT_PROCEDURE_ORDER_IDENTIFIER_SYSTEM);
            // Add resource Ids
            const procedureDiagnosticReportFhirIdMap: FhirResourceIdMap = {
              index: diagnosticReportProcedureIndex,
              fhirId: diagnosticResource.id
            };
            if (formCollection.offlisting.diagnosticReportProcedureFhirResourceIds) {
              formCollection.offlisting.diagnosticReportProcedureFhirResourceIds.push(procedureDiagnosticReportFhirIdMap);
            } else {
              formCollection.offlisting.diagnosticReportProcedureFhirResourceIds = [procedureDiagnosticReportFhirIdMap];
            }
            if (diagnosticReportProcedureMap[diagnosticReportProcedureIndex]) {
              //console.log('Multiple DRPs with the same index - investigate');
            } else {
              diagnosticReportProcedureMap[diagnosticReportProcedureIndex] = diagnosticResource;
            }
          }
          break;
        case 'Appointment':
          const appointment = resource as fhirR4.Appointment;
          listOfAppointments.push(appointment);
          break;
        case 'Procedure':
          const procedure = resource as fhirR4.Procedure;
          const procedureIndex =
          this.utilService.getIdentifierValueForGivenSystemUri(procedure.identifier, AppConstants.PROCEDURE_ORDER_IDENTIFIER_SYSTEM);
          const procedureFhirIdMap: FhirResourceIdMap = {
            index: procedureIndex,
            fhirId: procedure.id
          };

          formCollection.offlisting.procedureFhirResourceIds.push(procedureFhirIdMap);

          if (proceduresMap[procedureIndex]) {
            //console.log('Multiple procedures with the same index - investigate');
          } else {
            proceduresMap[procedureIndex] = procedure;
          }
          break;
      }
    });

    entryInformation.service = this.buildServiceDisplayData(serviceRequest, formCollection.service);

    // Wait Times
    if (!entryInformation.waitTime) {
      entryInformation.waitTime = [];
    }
    // Status
    if (serviceRequest.status) {
      let status = null;
      if (serviceRequest.status === 'active') {
        status = 'O';
      } else if (serviceRequest.status === 'completed') {
        status = 'C';
      }
      entryInformation.waitTime.push({
        linkId: AppConstants.entryStatusLinkId,
        value: status
      });

      entryInformation.status = status;
    }

    entryInformation.referral = this.buildReferralDisplayData(serviceRequest);

    this.initialSupportingInfoList = [];
    if (serviceRequest.supportingInfo) {
      this.initialSupportingInfoList = serviceRequest.supportingInfo;
      // Referral
      if (!entryInformation.referral) {
        entryInformation.referral = [];
      }
      const referralObservationsDisplayData: IDisplayData[] =
      this.processObservationsListForDisplayData(
        serviceRequest.supportingInfo,
        ClinicalConstants.referralObservationsList,
        formCollection.referral);
      referralObservationsDisplayData.forEach(item => entryInformation.referral.push(item));

      const waitTimesObservationsDisplayData: IDisplayData[] =
      this.processObservationsListForDisplayData(
        serviceRequest.supportingInfo,
        ClinicalConstants.waitTimesObservationsList,
        formCollection.waitTime);
      waitTimesObservationsDisplayData.forEach(item => entryInformation.waitTime.push(item));

      const offlistingObservationsDisplayData: IDisplayData[] =
      this.processObservationsListForDisplayData(
        serviceRequest.supportingInfo,
        ClinicalConstants.offlistingObservationsListForServiceRequest,
        formCollection.offlisting);
      offlistingObservationsDisplayData.forEach(item => entryInformation.offlisting.push(item));
    }

    if (diagnosticReport) {
      formCollection.diagnostics.diagnosticReportFhirResourceId = diagnosticReport.id;
      entryInformation.diagnostics = this.buildDiagnosticsDisplayData(diagnosticReport, formCollection.diagnostics);
    }

    if (listOfAppointments) {
      entryInformation.acceptance =
      this.buildAcceptanceDisplayData(listOfAppointments, serviceRequest, formCollection.acceptance);
    }

    if (serviceRequest.occurrencePeriod) {
      if (serviceRequest.occurrencePeriod.start) {
        const acceptanceDate = this.utilService.getFormattedDateFromGivenValue(serviceRequest.occurrencePeriod.start);
        const acceptanceDateDisplayValue: IDisplayData = {
          linkId: AppConstants.acceptanceDateLinkId,
          value: acceptanceDate
        };
        if (entryInformation.acceptance) {
          entryInformation.acceptance.push(acceptanceDateDisplayValue);
        } else {
          entryInformation.acceptance = [acceptanceDateDisplayValue];
        }
      }

      if (serviceRequest.occurrencePeriod.end) {
        const removalDate = this.utilService.getFormattedDateFromGivenValue(serviceRequest.occurrencePeriod.end);
        const removalDateDisplayValue: IDisplayData = {
          linkId: AppConstants.removalDateFieldLinkId,
          value: removalDate
        };
        if (entryInformation.offlisting) {
          entryInformation.offlisting.push(removalDateDisplayValue);
        } else {
          entryInformation.offlisting = [removalDateDisplayValue];
        }
      }
    }

    formCollection.offlisting.procedureObservationFhirResourceIds = [];
    const keys: string[] = Object.keys(proceduresMap);
    if (keys.length > 0) {
      for (const key of keys) {
        const procedureDisplayData: IDisplayData[] =
        this.buildOfflistingDisplayData(
          proceduresMap[key],
          diagnosticReportProcedureMap[key],
          key,
          formCollection.offlisting);
        procedureDisplayData.forEach(item => entryInformation.offlisting.push(item));
      }
    }

    this.setConditionalFields(entryInformation);

    this.setActualLinkIdsForAllSections(entryInformation, formCollection);

    return entryInformation;
  }

  private buildOfflistingDisplayData(
    procedure: fhirR4.Procedure,
    diagnosticReportProcedure: fhirR4.DiagnosticReport,
    procedureIndex: string,
    offlistingFormCollection: FormCollection): IDisplayData[] {
    const procedureDisplayData: IDisplayData[] = [];
    const index = this.getProcedureIndexFromOrderId(procedureIndex);
    let linkIdSuffix = '';
    if (index !== '0') {
      linkIdSuffix = '-' + index;
    }
    if (diagnosticReportProcedure?.result) {
      const diagnosticReportProcedureObservationReferenceList = diagnosticReportProcedure.result;
      // Fetch and populate the Obs details
      const procedureObservationDisplayData =
      this.processOfflistingObservationsForDisplayData(
        diagnosticReportProcedureObservationReferenceList,
        linkIdSuffix,
        offlistingFormCollection,
        procedureIndex);
      procedureObservationDisplayData?.forEach(item => procedureDisplayData.push(item));
    }

    // 115 Procedure status
    if (procedure.status) {
      let procedureStatusDisplayValue = null;
      if (procedure.status === AppConstants.procedureFHIRStatusCompleted) {
        procedureStatusDisplayValue = AppConstants.procedureStatusCompletedCode;
      } else {
        procedureStatusDisplayValue = AppConstants.procedureStatusNotDoneCode;
      }
      const procedureStatus: IDisplayData = {
        linkId: AppConstants.procedureStatusLinkId + linkIdSuffix,
        value: procedureStatusDisplayValue
      };
      procedureDisplayData.push(procedureStatus);
    }

    // 114 Procedure
    const procedureCodeValue = this.getCodeFromCodeableConcept(procedure.code, AppConstants.procedureCodeSystem);
    if (procedureCodeValue) {
      const procedureCode: IDisplayData = {
        linkId: AppConstants.allProceduresLinkId + linkIdSuffix,
        value: procedureCodeValue
      };
      procedureDisplayData.push(procedureCode);
    }

    // 116 Access site
    const accessSiteValue = this.getCodeFromCodeableConceptArray(procedure.bodySite, AppConstants.accessSiteCodeSystem);
    if (accessSiteValue) {
      const accessSite: IDisplayData = {
        linkId: AppConstants.accessSiteLinkId + linkIdSuffix,
        value: accessSiteValue
      };
      procedureDisplayData.push(accessSite);
    }

    // 157 & 158
    if (procedure.performer) {
      for (const procedurePerformer of procedure.performer) {
        const performerReference = procedurePerformer.actor.reference;
        const performerFunctionCode = this.getCodeFromCodeableConcept(procedurePerformer.function, AppConstants.performerCodeSystem);
        if (performerFunctionCode === AppConstants.treatingHealthCareProfessionalCode) {
          // 157 Treating HCP
          const treatingHCP: IDisplayData = {
            linkId: AppConstants.OffListingProcedureHCPLinkId + linkIdSuffix,
            value: performerReference
          };
          procedureDisplayData.push(treatingHCP);
        }
        if (performerFunctionCode === AppConstants.secondTreatingHealthCareProfessionalCode) {
          // 158
          const secondTreatingHCP: IDisplayData = {
            linkId: AppConstants.OffListingProcedureSecondHCPLinkId + linkIdSuffix,
            value: performerReference
          };
          procedureDisplayData.push(secondTreatingHCP);
        }
      }
    }

    return procedureDisplayData;
  }

  private setConditionalFields(entryInformation: IPatientEntryData) {
    // These settings are needed to trigger the display of enableWhen fields in their respective sections
    const serviceDetail2Field = entryInformation.service.find(item => item.linkId === AppConstants.serviceDetail2LinkId);
    if(serviceDetail2Field) {
      const serviceDetail2Value = serviceDetail2Field.value as string;
      if (serviceDetail2Value) {
        this.setServiceDetail2ForAllSections(serviceDetail2Value, entryInformation);
      }
    }

    // Diagnostics
    const serviceLocation = entryInformation.service.find(item => item.linkId === AppConstants.serviceLocationLinkId)?.value as string;
    const primaryReferralType =
    entryInformation.referral.find(item => item.linkId === AppConstants.primaryReasonReferralTypeLinkId)?.value as string;
    const secondaryReferralType =
    entryInformation.referral.find(item => item.linkId === AppConstants.secondaryReasonReferralTypeLinkId)?.value as string;
    this.setServiceValuesForDiagnosticSection(serviceLocation, primaryReferralType, secondaryReferralType, entryInformation);

    const historyOfCoronaryArteyBypassGraftSurgery =
    entryInformation.diagnostics.find(item =>
      item.linkId === AppConstants.historyOfCoronaryArteryBypassGraftSurgeryLinkId)?.value as string;
      // Offlisting
    this.setConditionalValuesForOfflistingSection(serviceLocation, primaryReferralType, historyOfCoronaryArteyBypassGraftSurgery, entryInformation);
  }

  private setConditionalValuesForOfflistingSection(serviceLocation: string,
    primaryReferralType: string,
    historyOfCoronaryArteyBypassGraftSurgery: string,
    entryInformation: IPatientEntryData) {
      if (serviceLocation) {
        if (entryInformation.offlisting) {
          entryInformation.offlisting.push({
            linkId: AppConstants.offlistingServiceLocation,
            value: serviceLocation
          });
        } else {
          entryInformation.offlisting = [{
            linkId: AppConstants.offlistingServiceLocation,
            value: serviceLocation
          }];
        }
      }
      if (primaryReferralType) {
        if (entryInformation.offlisting) {
          entryInformation.offlisting.push({
            linkId: AppConstants.offlistingPrimaryReferralType,
            value: primaryReferralType
          });
        } else {
          entryInformation.offlisting = [{
            linkId: AppConstants.offlistingPrimaryReferralType,
            value: primaryReferralType
          }];
        }
      }
      if (historyOfCoronaryArteyBypassGraftSurgery) {
        if (entryInformation.diagnostics) {
          entryInformation.offlisting.push({
            linkId: AppConstants.offlistingHistoryofCoronaryArteryBypass,
            value: historyOfCoronaryArteyBypassGraftSurgery
          });
        } else {
          entryInformation.offlisting = [{
            linkId: AppConstants.offlistingHistoryofCoronaryArteryBypass,
            value: historyOfCoronaryArteyBypassGraftSurgery
          }];
        }
      }
    }

  private setServiceValuesForDiagnosticSection(
    serviceLocation: string,
    primaryReferralType: string,
    secondaryReferralType: string,
    entryInformation: IPatientEntryData) {
    if (serviceLocation) {
      if (entryInformation.diagnostics) {
        entryInformation.diagnostics.push({
          linkId: AppConstants.diagnosticserviceLocation,
          value: serviceLocation
        });
      } else {
        entryInformation.diagnostics = [{
          linkId: AppConstants.diagnosticserviceLocation,
          value: serviceLocation
        }];
      }
    }
    if (primaryReferralType) {
      if (entryInformation.diagnostics) {
        entryInformation.diagnostics.push({
          linkId: AppConstants.diagnosticsPrimaryReferralType,
          value: primaryReferralType
        });
      } else {
        entryInformation.diagnostics = [{
          linkId: AppConstants.diagnosticsPrimaryReferralType,
          value: primaryReferralType
        }];
      }
    }
    if (secondaryReferralType) {
      if (entryInformation.diagnostics) {
        entryInformation.diagnostics.push({
          linkId: AppConstants.diagnosticSecondaryReferralType,
          value: secondaryReferralType
        });
      } else {
        entryInformation.diagnostics = [{
          linkId: AppConstants.diagnosticSecondaryReferralType,
          value: secondaryReferralType
        }];
      }
    }
  }

  private setServiceDetail2ForAllSections(serviceDetail2: string, entryInformation: IPatientEntryData) {
    if (entryInformation.referral) {
      entryInformation.referral.push({
        linkId: AppConstants.referralServiceDetail2,
        value: serviceDetail2
      });
    } else {
      entryInformation.referral = [{
        linkId: AppConstants.referralServiceDetail2,
        value: serviceDetail2
      }];
    }
    // Acceptance
    if (entryInformation.acceptance) {
      entryInformation.acceptance.push({
        linkId: AppConstants.acceptanceServiceDetail2,
        value: serviceDetail2
      });
    } else {
      entryInformation.acceptance = [{
        linkId: AppConstants.acceptanceServiceDetail2,
        value: serviceDetail2
      }];
    }
    // Diagnostics
    if (entryInformation.diagnostics) {
      entryInformation.diagnostics.push({
        linkId: AppConstants.diagnosticsServiceDetail2,
        value: serviceDetail2
      });
    } else {
      entryInformation.diagnostics = [{
        linkId: AppConstants.diagnosticsServiceDetail2,
        value: serviceDetail2
      }];
    }
  }

  private buildServiceDisplayData(
    serviceRequest: fhirR4.ServiceRequest,
    serviceFormCollection: FormCollection): IDisplayData[] {
    serviceFormCollection.serviceRequestFhirResourceId = serviceRequest.id;
    const serviceData: IDisplayData[] = [];
    const serviceCode = this.getCodeFromCodeableConcept(serviceRequest.code, AppConstants.serviceDetailCodeSystem) as string;
    const arr: string[] = serviceCode.split('.');
    const serviceCategory = arr[0];
    if (serviceCategory) {
      serviceData.push({
        linkId: AppConstants.entryCategoryLinkId,
        value: serviceCategory
      });
    }
    const serviceArea = arr[1];
    if (serviceArea) {
      serviceData.push({
        linkId: AppConstants.serviceAreaLinkId,
        value: serviceArea
      });
    }
    const serviceDetail1 = arr[2];
    if (serviceDetail1) {
      serviceData.push({
        linkId: AppConstants.serviceDetail1LinkId,
        value: serviceDetail1
      });
    }
    const serviceDetail2 = arr[3];
    if (serviceDetail2) {
      serviceData.push({
        linkId: AppConstants.serviceDetail2LinkId,
        value: serviceDetail2
      });
    }
    if (serviceRequest.performer) {
      // Service Location
      for (const performer of serviceRequest.performer) {
        if (performer.type === 'Organization') {
          // Build Service Location
          const serviceLocation = performer.reference;
          serviceData.push({
            linkId: AppConstants.serviceLocationLinkId,
            value: serviceLocation
          });
        }
      }
    }
    return serviceData;
  }

  private buildReferralDisplayData(serviceRequest: fhirR4.ServiceRequest): IDisplayData[] {
    const referralData: IDisplayData[] = [];
    // 33 Referral Date
    const referralDate = serviceRequest.authoredOn;
    if (referralDate) {
      referralData.push({
        linkId: AppConstants.referralDateLinkId,
        value: referralDate
      });
    }
    // 38 Primary Reason for Referral
    const referralPrimaryReason =
    this.getCodeFromCodeableConceptArray(serviceRequest.reasonCode, AppConstants.REFERRAL_REASON_CODE_SYSTEM);
    if (referralPrimaryReason) {
      referralData.push({
        linkId: AppConstants.primaryReasonReferralLinkId,
        value: referralPrimaryReason
      });
    }
    // 39 Primary Reason for Referral Type
    const referralPrimaryReasonType =
    this.getCodeFromCodeableConceptArray(serviceRequest.reasonCode, AppConstants.REFERRAL_REASON_TYPE_CODE_SYSTEM);
    if (referralPrimaryReasonType) {
      referralData.push({
        linkId: AppConstants.primaryReasonReferralTypeLinkId,
        value: referralPrimaryReasonType
      });
    }
    // 155 Referring Physician
    if (serviceRequest.requester) {
      const referringPhysician = serviceRequest?.requester.reference;
      referralData.push({
        linkId: AppConstants.referringPhysicianLinkId,
        value: referringPhysician
      });
    }
    // 160 Wait Location
    const supportingInfo = serviceRequest.supportingInfo;
    if (supportingInfo && supportingInfo.length > 0) {
      const waitLocation = supportingInfo.find(item => item.reference.includes('Organization'))?.reference;
      if (waitLocation) {
        referralData.push({
          linkId: AppConstants.waitLocationLinkId,
          value: waitLocation
        });
      }
    }
    return referralData;
  }

  updateAppointmentIds(entryId: string, acceptanceForm: FormCollection) {
    return new Promise<void>((res, rej) => {
      let listOfAppointmentIds: string[] = [];
      this.httpService.fetchEntryAppointments(entryId).then(async (data: fhirR4.Bundle) => {
        if (data.total > 0) {
          data.entry.forEach(entry => {
            listOfAppointmentIds.push((entry.resource as fhirR4.Appointment).id);
          });
          listOfAppointmentIds = listOfAppointmentIds.sort();
          acceptanceForm.appointmentFhirResourceIds = [];
          for (let index = 0; index < listOfAppointmentIds.length ; index++) {
            const appointmentFhirIdMap: FhirResourceIdMap = {
              index: index.toString(),
              fhirId: listOfAppointmentIds[index]
            };
            acceptanceForm.appointmentFhirResourceIds.push(appointmentFhirIdMap);
          }
          res();
        } else {
          acceptanceForm.appointmentFhirResourceIds = [];
          res();
        }
      });
    });
  }

  updateProcedureIds(entryId: string, offlistingForm: FormCollection) {
    const reportIds: FhirResourceIdMap[] = [];
    offlistingForm.procedureObservationFhirResourceIds = [];
    offlistingForm.diagnosticReportProcedureFhirResourceIds = [];
    offlistingForm.procedureFhirResourceIds = [];

    return new Promise<void>((res, rej) => {
      this.httpService.fetchEntryProcedures(entryId).then(async (data: fhirR4.Bundle) => {
        if (data.total > 0) {
          data.entry.forEach(entry => {
            if (entry.resource.resourceType === 'Procedure') {
              const procedure = entry.resource as fhirR4.Procedure;
              const procedureIndex =
          this.utilService.getIdentifierValueForGivenSystemUri(procedure.identifier, AppConstants.PROCEDURE_ORDER_IDENTIFIER_SYSTEM);
              const procedureFhirIdMap: FhirResourceIdMap = {
                index: procedureIndex,
                fhirId: procedure.id
              };
              offlistingForm.procedureFhirResourceIds.push(procedureFhirIdMap);
            } else if (entry.resource.resourceType === 'DiagnosticReport') {
              const diagnosticReportProcedure = entry.resource as fhirR4.DiagnosticReport;
              const diagnosticReportProcedureIndex = this.utilService.getIdentifierValueForGivenSystemUri(
                diagnosticReportProcedure.identifier,
                AppConstants.PROCEDURE_ORDER_IDENTIFIER_SYSTEM);
              const oneMap: FhirResourceIdMap = {
                index: diagnosticReportProcedureIndex,
                fhirId: entry.resource.id
              };
              reportIds.push(oneMap);
            }
          });
          if (reportIds.length > 0) {
            offlistingForm.diagnosticReportProcedureFhirResourceIds =
              offlistingForm.diagnosticReportProcedureFhirResourceIds.concat(reportIds);
            this.httpService.fetchDiagnosticReports(reportIds.map(x => x.fhirId)).then(async (reportData: fhirR4.Bundle) => {
              if (reportData.total > 0) {
                reportData.entry.forEach(entry => {
                  if (entry.resource.resourceType === 'Observation') {
                    const observation = entry.resource as fhirR4.Observation;
                    const observationCode = this.getObservationCodeFromCodeableConcept(observation.code);
                    let procedureIndex = '';
                    const identifier =
                    this.utilService.getIdentifierValueForGivenSystemUri(observation.identifier,
                      AppConstants.OBSERVATION_ORDER_IDENTIFIER_SYSTEM);
                    if (observationCode === AppConstants.stentObservationsCodeValue) {
                      procedureIndex = this.buildStentIndexFromOrderId(identifier);
                    } else {
                      procedureIndex = this.getProcedureIndexFromOrderId(identifier);
                    }
                    const observationMap: ObservationResourceIdMap = {
                      fhirId: observation.id,
                      observationCode,
                      index : procedureIndex,
                    };
                    offlistingForm.procedureObservationFhirResourceIds.push(observationMap);
                  }
                });
                res();
              } else {
                res();
              }
            });
          } else {
            res();
          }
        } else {
          offlistingForm.procedureFhirResourceIds = [];
          res();
        }
      });
    });

  }

  private buildDiagnosticsDisplayData(
    diagnosticReport: fhirR4.DiagnosticReport,
    diagnosticFormCollection: FormCollection): IDisplayData[] {
    diagnosticFormCollection.diagnosticReportFhirResourceId = diagnosticReport.id;
    const diagnosticsData: IDisplayData[] = [];
    // Objective is to build IDisplayData
    // Get list of Observations from Diagnostic Report
    const observationReferenceList: fhirR4.Reference[] = diagnosticReport.result;
    if (observationReferenceList?.length > 0) {
      const observationsDisplayData: IDisplayData[] =
      this.processObservationsListForDisplayData(
        observationReferenceList,
        ClinicalConstants.diagnosticsObservationList,
        diagnosticFormCollection);
      observationsDisplayData.forEach(item => diagnosticsData.push(item));
    }
    return diagnosticsData;
  }

  private buildAcceptanceDisplayData(
    listOfAppointments: fhirR4.Appointment[],
    serviceRequest: fhirR4.ServiceRequest,
    acceptanceFormCollection: FormCollection): IDisplayData[] {
    const acceptanceData: IDisplayData[] = [];
    // check if identifier property exists before sorting
    let existsIdentifiers = listOfAppointments.filter(function (o) {
      return o.hasOwnProperty('identifier');
    }).length; 
    if(existsIdentifiers > 0) {
      listOfAppointments.sort(
        (item1, item2) => this.compareAppointmentIndex(item1, item2)
      );
    }
  
    if (!acceptanceFormCollection) {
      return;
    }
 
    acceptanceFormCollection.appointmentFhirResourceIds = [];
    for (let index = 0; index < listOfAppointments.length ; index++) {
      const appointment = listOfAppointments[index];
      if (appointment.id) {
        const appointmentFhirIdMap: FhirResourceIdMap = {
          index: index.toString(),
          fhirId: appointment.id
        };
        acceptanceFormCollection.appointmentFhirResourceIds.push(appointmentFhirIdMap);
      }
      let linkIdSuffix = null;
      if (index !== 0) {
        linkIdSuffix = '-' + index;
      }
      if (appointment.cancelationReason) {
        const cancellationReason =
        this.getCodeFromCodeableConcept(appointment.cancelationReason, AppConstants.cancellationReasonCodeSystem);
        let cancellationReasonLinkId = AppConstants.cancellationsAndBookingsCancellationReasonLinkId;
        if (linkIdSuffix) {
          cancellationReasonLinkId = cancellationReasonLinkId + linkIdSuffix;
        }
        const cancellationDisplayData: IDisplayData = {
          linkId: cancellationReasonLinkId,
          value: cancellationReason,
          fhirResourceId: appointment.id
        };
        acceptanceData.push(cancellationDisplayData);
      }
      if (appointment.requestedPeriod) {
        const bookingDate = appointment.requestedPeriod[0].start;
        let requestedPeriodLinkId = AppConstants.bookingDateFieldLinkId;
        if (linkIdSuffix) {
          requestedPeriodLinkId = requestedPeriodLinkId + linkIdSuffix;
        }
        const bookingDateDisplayData: IDisplayData = {
          linkId: requestedPeriodLinkId,
          value: bookingDate,
          fhirResourceId: appointment.id
        };
        acceptanceData.push(bookingDateDisplayData);
      }
    }
    // Responsible HCP
    for (const performer of serviceRequest.performer) {
      if (performer.type === 'Practitioner') {
        // Build Responsible HCP
        const responsibleHealthCareProfessional = performer.reference;
        acceptanceData.push({
          linkId: AppConstants.responsibleHealthCareProfessionalLinkId,
          value: responsibleHealthCareProfessional
        });
      }
    }
    // Get list of Acceptance Observations from Service Request
    if (serviceRequest?.supportingInfo) {
      const acceptanceObservationsForAppointmentDisplayData: IDisplayData[] =
      this.processObservationsListForDisplayData(
        serviceRequest.supportingInfo,
        ClinicalConstants.acceptanceObservationsListForServiceRequest,
        acceptanceFormCollection);
      acceptanceObservationsForAppointmentDisplayData.forEach(item => acceptanceData.push(item));
    }
    return acceptanceData;
  }

  private processOfflistingObservationsForDisplayData(
    diagnosticReportProcedureObservationReferenceList: fhirR4.Reference[],
    linkIdSuffix: string,
    offlistingFormCollection: FormCollection,
    procedureIndex: string): IDisplayData[] {
    const displayData: IDisplayData[] = [];
    let mapIndex = '';

    for (const observationReference of diagnosticReportProcedureObservationReferenceList) {
      const observationId = this.utilService.getIdFromReference(observationReference.reference);
      const observation: fhirR4.Observation =
      this.observationsList.find(individualObservation => individualObservation.id === observationId);
      if (observation) {
        const code = this.getObservationCodeFromCodeableConcept(observation.code);
        // Add Resource Ids to Form Collection
        if (observationId) {
          this.addObservationResourceIdsToFormCollection(offlistingFormCollection, observationId, code, procedureIndex);
        }
        if (code === AppConstants.stentObservationsCodeValue) {
          const identifierValue =
          this.utilService.getIdentifierValueForGivenSystemUri(observation.identifier, AppConstants.OBSERVATION_ORDER_IDENTIFIER_SYSTEM);
          const suffixForStentIds = this.buildStentIndexFromOrderId(identifierValue);
          mapIndex = 'Procedure' +  suffixForStentIds;
          const stentDisplayData: IDisplayData[] = this.processAnswerStentObservations(observation, suffixForStentIds);
          stentDisplayData.forEach(item => displayData.push(item));
        } else {
          let linkId = ClinicalConstants.procedureObservationList.find(item => item.code === code)?.linkId;
          if (linkIdSuffix?.length > 0) {
            linkId = linkId + linkIdSuffix;
          }
          const value = this.processAnswerForObservation(observation);
          const displayDataItem: IDisplayData = {
            linkId: linkId,
            value: value
          };
          if (observationId) {
            displayDataItem.fhirResourceId = observationId;
          }
          displayData.push(displayDataItem);
          const identifierValue =
          this.utilService.getIdentifierValueForGivenSystemUri(observation.identifier, AppConstants.OBSERVATION_ORDER_IDENTIFIER_SYSTEM);

          mapIndex = 'Procedure' + this.getProcedureIndexFromOrderId(identifierValue);
        }
        const oneMap: ObservationResourceIdMap = {
          fhirId: observationId,
          index : mapIndex,
          observationCode: code
        };
        offlistingFormCollection.procedureObservationFhirResourceIds.push(oneMap);
      }
    }
    return displayData;
  }

  private addObservationResourceIdsToFormCollection(
    formCollection: FormCollection,
    fhirId: string,
    observationCode: string,
    index?: string) {
      const observationResourceIdMap: ObservationResourceIdMap = {
        fhirId: fhirId,
        observationCode: observationCode
      };
      if (index) {
        observationResourceIdMap.index = index;
      }
      if (formCollection.observationFhirResourceIds) {
        formCollection.observationFhirResourceIds.push(observationResourceIdMap);
      } else {
        formCollection.observationFhirResourceIds = [observationResourceIdMap];
      }
      // observationFhirResourceIds properties seems not used any where
    }

  private processAnswerStentObservations(
    observation: fhirR4.Observation,
    suffixForStentIds: string): IDisplayData[] {
    const stentDisplayData: IDisplayData[] = [];
    for (const component of observation.component) {
      const componentCode = this.getCodeFromCodeableConcept(component.code, AppConstants.stentObservationCodeSystem);
      if (componentCode === '127') {
        stentDisplayData.push(this.buildStentAnswer(observation.id, componentCode, component,
          suffixForStentIds, AppConstants.stentDeploymentLocationCodeSystem));
      } else if (componentCode === '128') {
        stentDisplayData.push(
          this.buildStentAnswer(observation.id, componentCode, component, suffixForStentIds, AppConstants.lesionTypeCodeSystem));
      } else if (componentCode === '129') {
        stentDisplayData.push(
          this.buildStentAnswer(observation.id, componentCode, component, suffixForStentIds, AppConstants.stentTypeCodeSystem));
      } else if (componentCode === '130') {
        stentDisplayData.push(this.buildStentAnswer(observation.id, componentCode, component, suffixForStentIds));
      } else if (componentCode === '131') {
        stentDisplayData.push(this.buildStentAnswer(observation.id, componentCode, component, suffixForStentIds));
      }
      else if (componentCode === '177') {
        stentDisplayData.push(
          this.buildStentAnswer(observation.id, componentCode, component, suffixForStentIds, AppConstants.CHRONIC_TOTAL_OCCLUSION_RESULT_CODE_SYSTEM));
      }
    }
    return stentDisplayData;
  }

  private buildStentAnswer(
    observationId: string,
    componentCode: string,
    component: fhirR4.ObservationComponent,
    suffixForStentIds: string,
    codeSystemForComponent?: string): IDisplayData {
    const linkId = ClinicalConstants.stentElements.find(item => item.code === componentCode)?.linkId;
    let value = null;
    if (component.valueCodeableConcept) {
      value = this.getCodeFromCodeableConcept(component.valueCodeableConcept, codeSystemForComponent);
    } else if (component.valueQuantity) {
      value = component.valueQuantity.value;
    } else if (Number.isInteger(component.valueInteger) || this.utilService.isNumber(component.valueInteger?.toString())) {
      value = component.valueInteger;
    }
    const displayDataItem: IDisplayData = {
      linkId: linkId + suffixForStentIds,
      value: value,
      fhirResourceId: observationId
    };
    return displayDataItem;
  }

  private processObservationsListForDisplayData(
    observationReferenceList: fhirR4.Reference[],
    listOfObservationCodesAndLinkIds: ICodeAndLinkIdForObservations[],
    formCollection: FormCollection): IDisplayData[] {
    const displayData: IDisplayData[] = [];
    let dartsIndex = 0;
    for (const observationReference of observationReferenceList) {
      const observationId = this.utilService.getIdFromReference(observationReference.reference);
      const observation: fhirR4.Observation = this.observationsList.find(item => item.id === observationId);
      if (observation) {
        const value = this.processAnswerForObservation(observation);
        const code = this.getObservationCodeFromCodeableConcept(observation.code);

        // Get linkId from List
        let linkId = listOfObservationCodesAndLinkIds.find(item => item.code === code)?.linkId;
        if (linkId) {
          if (code === AppConstants.DART_FROM_OBSERVATION_CODE) {
            // DARTs are saved as a single item with a code of DART From, i.e 108
            // Split this into two displayData objects
            const valuePeriod = observation.valuePeriod;
            const dartItems = this.buildDARTItemFromObservation(observationId, linkId, valuePeriod, dartsIndex);
            dartItems.forEach(item => displayData.push(item));
            dartsIndex++;
          } else if (code === AppConstants.DELAY_REASON_OBSERVATION_CODE || code === AppConstants.RACE_OBSERVATION_CODE) {
            if (code === AppConstants.DELAY_REASON_OBSERVATION_CODE) {
              // Delay Reasons
              const delayReasonLinkId = this.buildDelayReasonAndRaceLinkId(value as string, AppConstants.delayReasonLinkId);
              const item: IDisplayData = {
                fhirResourceId: observationId,
                linkId: delayReasonLinkId,
                value
              };
              displayData.push(item);
            } else {
              // Race
              const raceLinkId = this.buildDelayReasonAndRaceLinkId(value as string, AppConstants.raceLinkId);
              const item: IDisplayData = {
                fhirResourceId: observationId,
                linkId: raceLinkId,
                value
              };
              displayData.push(item);
            }
          } else if (code === AppConstants.PATIENT_PRESENTATION_OBSERVATION_CODE) {
            // #3342 - Patient Presentation
            const item: IDisplayData = {
              fhirResourceId: observationId,
              linkId: linkId,
              value
            };
            displayData.push(item);
            // Non-Percutaneous Coronary Intervention Capable Hospital
            if (observation.performer) {
              const valueReference = observation.performer[0].reference;
              const nonPercutaneousCoronaryInterventionCapableHospitalItem: IDisplayData = {
                fhirResourceId: observationId,
                linkId: AppConstants.nonPercutaneousHospitalLinkId,
                value: valueReference
              };
              displayData.push(nonPercutaneousCoronaryInterventionCapableHospitalItem);
            }
          } else {
            if (observation.valueDateTime || observation.dataAbsentReason) {
              let dataAbsentReasonCode = null;
              if (observation.dataAbsentReason) {
                if (value === AppConstants.UNKNOWN_ENTRY_VALUE_CODE) {
                  dataAbsentReasonCode = AppConstants.UNKNOWN_ENTRY_VALUE_TEXT;
                } else if (value === AppConstants.NOT_APPLICABLE_ENTRY_VALUE) {
                  dataAbsentReasonCode = AppConstants.NOT_APPLICABLE_DISPLAY_VALUE;
                }
              }
              const dateTimeDisplayItems: IDisplayData[] =
              this.buildDateAndTimeDisplayFieldsForEachDateTimeField(linkId, value as string, observationId, dataAbsentReasonCode);
              dateTimeDisplayItems.forEach(item => displayData.push(item));
            } else {
              const item: IDisplayData = {
                fhirResourceId: observationId,
                linkId: linkId,
                value: value
              };
              displayData.push(item);
            }
          }
          // Add Resource Ids to Form Collection
          if (observationId) {
            this.addObservationResourceIdsToFormCollection(formCollection, observationId, code);
          }
        }
      }
    }
    return displayData;
  }

  private buildDateAndTimeDisplayFieldsForEachDateTimeField(
    linkId: string,
    value: string,
    observationId: string,
    dataAbsentReasonCode: string): IDisplayData[] {
    // DateTime format is YYYY-MM-DDThh:mm:ss+zz:zz
    // iso value, not local value
    const retVal: IDisplayData[] = [];
    const dateTimeItem: IDisplayData = {
      fhirResourceId: observationId,
      linkId: linkId,
      value: value,
      absentReason: dataAbsentReasonCode
    };
    retVal.push(dateTimeItem);
    return retVal;
  }

  private buildDelayReasonAndRaceLinkId(value: string, baseLinkId: string): string {
    let linkId = baseLinkId;
    if (value === AppConstants.indigenousRaceDisplayValue) {
      linkId = linkId + '-' + 'Indigenous'.toLowerCase().replace(/\s+/g, '-');
    } else {
      // Replace spaces and slashes with hyphens
      linkId = linkId + '-' + value.toLowerCase().replace(/\s+/g, '-').replace(/[\\|\/]/, '-');
    }
    return linkId;
  }

  private buildDARTItemFromObservation(
    observationId: string,
    linkId: string,
    valuePeriod: fhirR4.Period,
    dartsIndex: number): IDisplayData[] {
    //console.log('Darts index: ' + dartsIndex);
    let dartsLinkIdSuffix = null;
    if (dartsIndex !== 0) {
      dartsLinkIdSuffix = '-' + dartsIndex;
    }
    const retVal: IDisplayData[] = [];
    let dartFromLinkId = linkId;
    if (dartsLinkIdSuffix) {
      dartFromLinkId = dartFromLinkId + dartsLinkIdSuffix;
    }
    const dartFrom: IDisplayData = {
      fhirResourceId: observationId,
      linkId: dartFromLinkId,
      value: valuePeriod.start
    };
    retVal.push(dartFrom);

    // Build linkId of DART to
    let dartToLinkId = AppConstants.datesAffectingReadinessToTreatToDateLinkId;
    if (dartsLinkIdSuffix) {
      dartToLinkId = dartToLinkId + dartsLinkIdSuffix;
    }
    const dartTo: IDisplayData = {
      fhirResourceId: observationId,
      linkId: dartToLinkId,
      value: valuePeriod.end
    };
    retVal.push(dartTo);
    return retVal;
  }

  private processAnswerForObservation(observation: fhirR4.Observation): string | Date | boolean | number {
    let retVal = null;
    const systmeUrls = [AppConstants.observationCodeSystemUri, AppConstants.WAIT_TIMES_AND_METRICS_OBSERVATIONS_CODE_SYSTEM]
    const codeableConcept: fhirR4.CodeableConcept = observation.code;
    for (const coding of codeableConcept.coding) {
      if (systmeUrls.includes(coding.system)) {
        const observationCode = coding.code;
        retVal = this.getValueFromGivenObservation(observation, observationCode);
        // #3267 - Patient Postal Code
        if (observationCode === AppConstants.PATIENT_REFERRAL_POSTAL_CODE_OBSERVATION_CODE) {
          this.patientPostalCodeOriginalValue = retVal;
        }
      }
    }
    return retVal;
  }

  getValueFromGivenObservation(observation: fhirR4.Observation, observationCode: string): string | Date | boolean | number | fhirR4.Period{
    if (observation.dataAbsentReason) {
      const clinicalDataAbsentReason =
      this.getCodeFromCodeableConcept(observation.dataAbsentReason, AppConstants.DATA_ABSENT_REASON_CODE_SYSTEM);
      const entryDataAbsentReason =
      this.clinicalAdapterService.convertClinicalDataAbsentReasonToEntryValues(clinicalDataAbsentReason, observationCode);
      return entryDataAbsentReason;
    } else {
      if (observation.valueString) {
        return observation.valueString;
      } else if (observation.valueDateTime) {
        const regx = new RegExp('(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})-(\d{2}):(\d{2})');

        if (regx.test(observation.valueDateTime)) {
          // Remove timezone offset for display
          // Expected format of timezone is YYYY-MM-DDThh:mm:ss-zz:zz
          const indexOfHyphen = observation.valueDateTime.lastIndexOf('-');
          return observation.valueDateTime.substring(0, indexOfHyphen);
        } else {
          // Some dates like clinical status change date in YYYY-MM-DD format are stored as valueDateTime
          // They should be returned as is
          return observation.valueDateTime;
        }
      } else if (observation.valueBoolean || observation.valueBoolean === false) {
        const valueBoolean = this.clinicalAdapterService.convertBooleanValuesToEntryValues(observation.valueBoolean);
        return valueBoolean;
      } else if (observation.valueCodeableConcept) {
        const clinicalValue = this.getCodeFromCodeableConceptWithoutSystem(observation.valueCodeableConcept);
        const code = this.clinicalAdapterService.getEntryValueFromClinicalValue(observationCode, clinicalValue);
        return code;
      } else if (Number.isInteger(observation.valueInteger)) {
        return observation.valueInteger;
      } else if (observation.valueQuantity) {
        return observation.valueQuantity.value;
      }
      else if (observation.valuePeriod) {
        return observation.valuePeriod;
      }
    }
  }

  getValueFromObservationWithGivenCode(code: string, observationsList: fhirR4.Observation[]): string | Date | boolean | fhirR4.Period {
    let retVal = null;
    for (const observation of observationsList) {
      const observationCodeValue = this.getObservationCodeFromCodeableConcept(observation.code);
      if (observationCodeValue === code) {
        retVal = this.getValueFromGivenObservation(observation, observationCodeValue);
        return retVal;
      }
    }
    return retVal;
  }

  /*
  This method sets the LinkIds that match the ones in the Questionnaire
  As the Entry information is saved in Clinical resources and not questionnaire responses anymore,
  actual LinkIds need to be fetched from the Questionnaire to populate the IPatientEntryData
  */
  private setActualLinkIdsForAllSections(entryInformation: IPatientEntryData, formCollection: FormCollectionSet) {
    this.setIndividualSectionActualLinkId(entryInformation.service, formCollection.service);
    if (entryInformation.referral) {
      this.setIndividualSectionActualLinkId(entryInformation.referral, formCollection.referral);
    }
    if (entryInformation.diagnostics) {
      this.setIndividualSectionActualLinkId(entryInformation.diagnostics, formCollection.diagnostics);
    }
    if (entryInformation.acceptance) {
      this.setIndividualSectionActualLinkId(entryInformation.acceptance, formCollection.acceptance);
    }
  }

  private setIndividualSectionActualLinkId(sectionDisplayData: IDisplayData[], formCollection: FormCollection) {
    for (const displayDataItem of sectionDisplayData) {
      if (displayDataItem.value) {
        formCollection.data.forEach(item => {
          // Find item that is of the type dropdown and make sure that the linkId matches the linkId of the display item
          // The 2nd condition is necessary as some items like primary/secondary referral reason/reason type have matching answer options
          if (item.controlType === 'dropdown' && item.linkId.includes(displayDataItem.linkId)) {
            item.fhirResourceId = displayDataItem.fhirResourceId;
            const dropdownInput = item as DropdownInput;
            const dropDownOption = dropdownInput.options.find(option => option.code === displayDataItem.value);
            if (dropDownOption) {
              displayDataItem.linkId = item.linkId;
            }
          }
        });
      }
    }
  }

  private buildIdentifierForProcedureElements(index: string, identifierSystem: string): fhirR4.Identifier {
    const identifier: fhirR4.Identifier = {
      system: identifierSystem
    };
    let indexForIdentifier = (+index + 1).toString();
    if (indexForIdentifier.length === 1) {
      indexForIdentifier = '0' + indexForIdentifier;
    }
    identifier.value = indexForIdentifier;
    return identifier;
  }

  private addIdentifierValueForProcedureElements(
    identifierValue: string,
    identifierSystem: string,
    identifierArray: fhirR4.Identifier[]): fhirR4.Identifier[] {
    let retVal: fhirR4.Identifier[] = [];
    if (identifierArray?.length > 0) {
      for (const identifier of identifierArray) {
        if (identifier.system === identifierSystem) {
          identifier.value = identifierValue;
        }
      }
      retVal = identifierArray;
    } else {
      const identifier: fhirR4.Identifier = {
        system: identifierSystem,
        value: identifierValue
      };
      retVal.push(identifier);
    }
    return retVal;
  }

  // Increment the index by 1, and pad zero to make the length of the index 2
  // Sample input: 1, output: 02
  // Sample input: 0-1, output: 0102
  private buildIndexForProcedureElements(index: string): string {
    let retVal: string = null;
    if (index.includes('-')) {
      // Stent components
      const indexOfHyphen = index.indexOf('-');
      let procedureIndex = index.substring(0, indexOfHyphen);
      procedureIndex = this.buildIndexForProcedureElements(procedureIndex);
      let stentIndex = index.substring(indexOfHyphen + 1);
      stentIndex = (+stentIndex + 1).toString();
      if (stentIndex.length === 1) {
        stentIndex = '0' + stentIndex;
      }
      retVal = procedureIndex + stentIndex;
    } else {
      retVal = (+index + 1).toString();
      if (retVal.length === 1) {
        retVal = '0' + retVal;
      }
    }
    return retVal;
  }

  private buildIndexForStentElements(index: string): string {
    let retVal: string = null;
    if (index.includes('-')) {
      // Stent components
      const indexOfHyphen = index.indexOf('-');
      let procedureIndex = index.substring(0, indexOfHyphen);
      procedureIndex = this.buildIndexForProcedureElements(procedureIndex);
      let stentIndex = index.substring(indexOfHyphen + 1);
      stentIndex = (+stentIndex + 1).toString();
      if (stentIndex.length === 1) {
        stentIndex = '0' + stentIndex;
      }
      retVal = procedureIndex + stentIndex;
    } else {
      retVal = (+index + 1).toString();
      if (retVal.length === 1) {
        retVal = '0' + retVal + '01';
      }
    }
    return retVal;
  }

  private getProcedureIndexFromOrderId(stringValueOfProcedureIndex: string): string {
    // Remove Entry Id to get the index
    // If length of the index is 2, its a non-stent Order ID
    // If length of the index is 4, its a stent Order ID
    let retVal: number;
    if (stringValueOfProcedureIndex.length === 2) {
      retVal = +stringValueOfProcedureIndex - 1;
    } else if (stringValueOfProcedureIndex.length === 4) {
      const procedureIndex = stringValueOfProcedureIndex.substring(0, 2);
      retVal = +procedureIndex - 1;
    }

    if(retVal){
      return retVal.toString();
    }else{
      return '0';
    }
  }

  private buildStentIndexFromOrderId(orderedIndex: string): string {
    let retVal: string = null;
    let stentIndex: string = null;
    stentIndex = orderedIndex.substring(2);
    const procedureIndex = orderedIndex.substring(0, 2);
    const procedureIndexAsNumber = +procedureIndex - 1;
    const stentIndexAsNumber = (+stentIndex === 0) ? 0 : (+stentIndex - 1);
    if (stentIndexAsNumber === 0) {
      retVal = '-' + procedureIndexAsNumber.toString();
    } else {
      retVal = '-' + procedureIndexAsNumber.toString() + '-' + stentIndexAsNumber.toString();
    }
    return retVal;
  }

  private buildReferenceListFromBundleEntries(observationsEntriesList: fhirR4.BundleEntry[], fhirResourceType: string): fhirR4.Reference[] {
    const retVal: fhirR4.Reference[] = [];
    for (const entry of observationsEntriesList) {
      const reference = this.buildReferenceForSingleBundleEntry(entry, fhirResourceType);
      if (reference) {
        retVal.push(reference);
      }
    }
    return retVal;
  }

  private buildReferenceForSingleBundleEntry(bundleEntry: fhirR4.BundleEntry, fhirResourceType: string): fhirR4.Reference {
    const reference = new fhirR4.Reference;
    if (bundleEntry && bundleEntry.resource) {
      if (bundleEntry.resource.id) {
        reference.reference = fhirResourceType + '/' + bundleEntry.resource.id;
      } else {
        reference.reference = bundleEntry.fullUrl;
      }
    } else {
      return null;
    }
    return reference;
  }

  private getAppointmentIndex(item: fhirR4.Appointment): number {
    let value: string = this.utilService.getIdentifierValueForGivenSystemUri(item.identifier, AppConstants.APPOINTMENT_IDENTIFIER_SYSTEM);
    return parseInt(value);
  }

  private compareAppointmentIndex (item1: fhirR4.Appointment, item2: fhirR4.Appointment): number {
    return this.getAppointmentIndex(item1) - this.getAppointmentIndex(item2);
  };

}
